import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import axios from 'axios'
import { ChatMessage, Artifact, ChatContext as ChatContextType, Patient, FileCitation } from '../types/chat'
import { backendAdapter, uploadWorkspaceFile } from '../services/backendAdapter'
import { validateFile } from '../utils/fileValidation'
import { describeSaveError } from '../utils/describeSaveError'
import api, { getAuthToken } from '../services/api'
import { debugLog } from '../utils/debugLogger'
import { THINKING_PHASE_SEPARATOR, beginThinkingPhase, finalizeThinkingPhase } from '../utils/thinkingPhase'
import { useSubscription } from './SubscriptionContext'
import { workspaceService } from '../services/workspaceService'
import type { MedsStatus } from '../components/patient-panel/medsStatus'

const WS_CONVERSATION_MAP_KEY = 'caseysPanel.workspaceConversations'

/**
 * Format tool name into user-friendly display name.
 * Exported so the case panel's StreamingProgressPanel renders identical tool
 * labels to the regular chat (the panel maps per-agent AgentToolCall → ToolCallStatus).
 */
export const formatToolDisplayName = (toolName: string): string => {
  const displayNames: Record<string, string> = {
    'search_pubmed': 'Searching PubMed',
    'search_rxnorm': 'Searching RxNorm',
    'search_clinical_trials': 'Searching ClinicalTrials.gov',
    'search_fda_adverse_events': 'Searching FDA FAERS',
    'lookup_drug_label': 'Looking up FDA Label',
    'search_web': 'Searching Guidelines',
    'search_open_evidence': 'Searching OpenEvidence',
    'load_skill': 'Loading Skill',
    'get_patient_data': 'Reading Patient Data',
    'update_patient_data': 'Updating Patient Data',
    'create_artifact': 'Creating Document',
    'update_artifact': 'Updating Document',
    // Case panel tools
    'save_clinical_data': 'Clinical data saved',
    'save_extractions': 'Clinical data extracted',
    'save_chart_data': 'Chart data saved',
    'load_chart_data': 'Chart data loaded',
    'save_structured_chart': 'Structured chart saved',
    'load_structured_chart': 'Structured chart loaded',
    'save_hpi_pe': 'HPI & Physical Exam saved',
    'load_hpi_pe': 'HPI & Physical Exam loaded',
    'save_ap': 'Assessment & Plan saved',
    'load_ap': 'Assessment & Plan loaded',
    'save_note': 'Clinical note saved',
    'load_notes': 'Clinical notes loaded',
    'generate_hpi_pe': 'HPI & Physical Exam generated',
    'generate_ap': 'Assessment & Plan generated',
    'reconcile_meds': 'Medication reconciliation complete',
    'determine_billing': 'Billing codes determined',
    'drug_interaction_check': 'Drug interaction check',
    'gap_analysis': 'Quality gap analysis',
    'search_clinical_evidence': 'Clinical evidence search',
  }

  return displayNames[toolName] || toolName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Prompt for reconciling patient data with conversation and documents
 */
const RECONCILE_PATIENT_DATA_PROMPT = `
I need you to reconcile the patient data panel with all available information.

**Instructions:**
1. Use \`get_patient_data("all")\` to retrieve the current patient panel data
2. Review all conversation messages and attached documents for clinical information
3. Compare what's in the panel vs. what's mentioned elsewhere
4. Present a clear DIFF showing:
   - **Missing data**: Information in documents/conversation NOT in the panel
   - **Outdated data**: Information in panel that may need updating
   - **Conflicting data**: Discrepancies between sources
5. Format your findings as a clear proposal for my review
6. Wait for my confirmation before making any changes with \`update_patient_data\`

Please begin the reconciliation analysis now.
`.trim()

/**
 * Detect pipeline-automated user messages by their content patterns.
 * These are messages sent by pipelineManager.ts, not typed by the physician.
 */
const isPipelineUserMessage = (content: string): boolean => {
  const pipelinePrefixes = [
    "Process this patient's chart data for admission",
    'INTAKE: Generate a complete HPI and Physical Exam',
    'The Historian has finished generating the HPI+PE',
    '[FLOW_STEP:',
  ]
  return pipelinePrefixes.some(prefix => content.startsWith(prefix))
}

/**
 * Message update type that allows content to be a function updater
 */
type MessageUpdate = Partial<ChatMessage> | ((msg: ChatMessage) => Partial<ChatMessage>) | {
  content: string | ((prev: string) => string)
  [key: string]: any
}

/**
 * Backend message response structure from session API
 */
interface BackendMessage {
  id: string
  role: string
  content: string
  artifact_id?: string | null
  artifact_type?: string | null
  seq: number
  created_at: string
  metadata?: Record<string, unknown> | null
  thinking_content?: string | null
  thinking_duration_ms?: number | null
}

/**
 * Pure function to transform backend message to frontend format.
 * Separates data transformation from IO for better testability.
 * 
 * @param msg - Backend message from API
 * @returns Formatted ChatMessage for frontend state
 */
const formatBackendMessage = (msg: BackendMessage): ChatMessage => ({
  id: msg.id,
  role: msg.role as ChatMessage['role'],
  content: msg.content,
  timestamp: msg.created_at,
  seq: msg.seq,
  artifact: msg.artifact_id ? {
    type: (msg.artifact_type || 'custom') as 'hpi' | 'assessment' | 'sweep' | 'ipass' | 'custom' | 'chart_sweep',
    title: '',
    content: '',
    artifact_id: msg.artifact_id
  } : undefined,
  fileCitations: (msg.metadata as any)?.fileCitations || undefined,
  metadata: msg.metadata as ChatMessage['metadata'],
  thinkingContent: msg.thinking_content ?? undefined,
  thinkingDurationMs: msg.thinking_duration_ms ?? undefined,
  attachments: (msg.metadata as any)?.attachments
})

/**
 * Backend session response structure
 */
interface SessionResponse {
  id: string
  title: string
  status: string
  created_at: string
  last_activity: string
  message_count: number
}

export interface UnifiedFile {
  id: string
  name: string
  type: string
  size: number
  status: 'uploading' | 'processing' | 'ready' | 'error'
  progress: number
  blobUrl?: string
  extractedText?: string
  parsingStatus?: 'pending' | 'quick_done' | 'complete' | 'error'
  extractionPhase?: 'extraction' | 'reconciliation' | 'safety_check'
  source: 'dropzone' | 'paperclip'
  addedAt: number
  error?: string
  rawFile?: File  // Original File object for vector store upload
}

/**
 * PubMed citation from tool call
 */
export interface PubMedCitation {
  number: number
  pmid: string
  title: string
  authors_short: string
  journal: string
  year: string
  url: string
  abstract_snippet?: string
}

/**
 * Research status for UI feedback during PubMed searches
 */
interface ResearchStatus {
  active: boolean
  message: string
}

/**
 * Tool call status for rich streaming UX
 */
export interface ToolCallStatus {
  id: string
  toolName: string
  displayName: string
  status: 'running' | 'complete' | 'error'
  args?: { query?: string; skill_name?: string }
  progress?: string
  resultSummary?: string
  citationCount?: number
  startedAt: number
}

/**
 * Streaming progress state for rich UX during tool calls
 */
interface StreamingProgress {
  toolCalls: ToolCallStatus[]
  currentPhase: 'idle' | 'thinking' | 'searching' | 'generating'
  thinkingContent: string      // Accumulated thinking content
  isThinking: boolean          // Currently in thinking phase
}

/**
 * View mode for main content area
 */
type ViewMode = 'chat' | 'project-detail'

/**
 * Semantic extraction from LLM parsing
 * Flexible structure that preserves clinical nuance with temporal context
 */
export interface SemanticExtraction {
  category: 'medication' | 'problem' | 'allergy' | 'vital' | 'lab' |
            'imaging' | 'procedure' | 'finding' | 'plan' | 'social' |
            'family' | 'other'
  content: string
  context: string  // "current", "discharge", "home medication", "historical", etc.
  confidence: number

  // Problem-specific
  priority?: number
  status?: 'worsening' | 'stable' | 'improving' | 'resolved' | 'unknown'
  supporting_evidence?: string[]
  next_action?: string

  // Lab-specific
  is_abnormal?: boolean
  trend_direction?: 'up' | 'down' | 'stable'
  prior_values?: string[]

  // Medication-specific
  is_high_risk?: boolean
  high_risk_category?: string
  requires_monitoring?: string
  change_status?: 'new' | 'changed' | 'held' | 'discontinued' | 'continued'
}

/**
 * Allergy conflict detected between patient allergies and medications
 */
export interface AllergyConflict {
  allergy: string
  conflicting_med: string
  severity: 'critical' | 'warning'
  reason?: string
}

/**
 * Patient clinical data - flexible LLM-first schema
 * Core principle: Preserve nuance, don't force into rigid boxes
 */
export interface PatientClinicalData {
  id: string                    // Database public_id (UUID)

  // Quick extraction results (always present first)
  patient_summary: string       // One-line: age, sex, key conditions, reason for encounter
  allergies: string[]           // All allergies. 'NKDA' if none, 'Not documented' if unclear
  code_status: string | null    // Full code, DNR, DNR/DNI, Comfort care, or null
  source_description: string    // What documents are included

  // Deep extraction results
  extractions: SemanticExtraction[]

  /** @deprecated Legacy blob — replaced by structured clinical sections below. Remove once old workspaces are migrated. */
  panel_content?: string | null

  // Structured clinical sections (preferred over panel_content)
  vitals?: unknown[] | null
  labs?: unknown[] | null
  imaging?: unknown[] | null
  orders?: Record<string, unknown> | null
  medications?: Record<string, unknown> | null
  summary_presentation?: string | null
  summary_admission_context?: string | null
  med_recon_status?: MedsStatus

  // Safety alerts
  allergy_conflicts?: AllergyConflict[]

  // Metadata
  extraction_model: string
  created_at: string
  updated_at: string

  // Linkage (may be null for user-scoped)
  conversation_id?: string
  project_id?: string
}

/**
 * Per-agent chat message used in case panel multi-agent sessions.
 * Lighter than ChatMessage — no artifact/citation overhead.
 */
export interface AgentChatMessage {
  id: string
  role: 'user' | 'assistant' | 'error'
  content: string
  agentId?: string
  tools: AgentToolCall[]
  canvasUpdates?: CanvasUpdate[]
  isStreaming?: boolean
  timestamp: number
  /** True for messages originating from pipeline automation (not physician-typed) */
  fromPipeline?: boolean
  /** LLM reasoning chain (live + persisted). Undefined ⇒ no reasoning to show. */
  thinkingContent?: string
  /** Total reasoning duration in ms; set once reasoning completes. */
  thinkingDurationMs?: number
  /** True only while a reasoning phase is actively streaming (live; never persisted). */
  isThinking?: boolean
  /**
   * Advisory specialist consults embedded in THIS Casey turn (the relay feature).
   * Present only on Casey assistant messages that dispatched ≥1 consult. Each is
   * rendered as a collapsible "consult pill" INSIDE Casey's bubble (handoff chip →
   * pill → Casey's synthesis), never as a separate persona bubble. A multi-hop
   * turn (cap = 2) carries one entry per consult, in dispatch order.
   */
  consult?: ConsultState[]
}

/**
 * State of an advisory agent-to-agent consult, surfaced as a pill in Casey's turn.
 * `content` streams live from the consult leg and is rehydrated on reload from the
 * persisted consult row (`metadata.agentId` + `metadata.consultReason`).
 */
export interface ConsultState {
  /** Allowlisted specialist consulted: 'hpi-pe-agent' (Historian) | 'clinical-reasoning' (Resident). */
  agent: string
  /** Advisory reason from the relay marker (relay_start.data.reason / metadata.consultReason). */
  reason?: string
  /** The specialist's advisory text — streamed live, persisted for reload. */
  content: string
  /** 'streaming' while the consult leg runs; 'done' on success; 'dropped' on failure/timeout/cap. */
  status: 'streaming' | 'done' | 'dropped'
}

/**
 * Settle every still-`streaming` consult to a terminal status, leaving already
 * -terminal entries untouched (so a late `relay_dropped` can't downgrade a
 * consult that already finished). Used by relay_start (prior consult → done as a
 * new one begins), relay_dropped (rejection → done, genuine failure → dropped),
 * and the clean-complete finalize.
 */
const settleStreamingConsults = (
  consults: ConsultState[],
  status: 'done' | 'dropped'
): ConsultState[] =>
  consults.map(c => (c.status === 'streaming' ? { ...c, status } : c))

/**
 * Settle still-`streaming` consults on a TURN-level interrupt (concurrent_request
 * early-return, thrown stream error, abort). The consult itself may have finished
 * (full advisory streamed) or been cut off: a non-empty consult keeps its advisory
 * and settles 'done'; an empty one (no tokens) becomes 'dropped' so the pill reads
 * "Consult unavailable" instead of a green check over an empty body.
 */
const settleConsultsOnInterrupt = (consults: ConsultState[]): ConsultState[] =>
  consults.map(c =>
    c.status === 'streaming'
      ? { ...c, status: c.content.trim() ? 'done' : 'dropped' }
      : c
  )

/**
 * Append a consult-leg delta to the active (last `streaming`) consult. No-op if
 * none is streaming (defensive: tokens should never precede `relay_start`).
 */
const appendToActiveConsult = (consults: ConsultState[], delta: string): ConsultState[] => {
  let active = -1
  for (let i = consults.length - 1; i >= 0; i--) {
    if (consults[i].status === 'streaming') { active = i; break }
  }
  if (active === -1) {
    // Advisory text is being DROPPED — a backend ordering regression (token
    // before/after the pill's streaming window) must be diagnosable, not silent.
    console.warn('[consult] dropped advisory delta: no consult is streaming')
    return consults
  }
  return consults.map((c, i) => (i === active ? { ...c, content: c.content + delta } : c))
}

export interface AgentToolCall {
  name: string
  input: string
  output: string
  isRunning?: boolean
  /** Full provider call_id from the backend; join key for tool_end. */
  callId?: string
  /** Tool success flag from tool_end; false renders the error state. */
  success?: boolean
  /** Whitelisted non-PHI arg preview from tool_start (e.g. query, skill_name). */
  args?: Record<string, unknown>
  /** Citation count from tool_end; drives the "N found" badge. */
  citationCount?: number
  /** Display label for synthetic skill-loaded rows (e.g. "Loaded Skill: cardiology"). */
  skillLabel?: string
  /** #267: true when this tool built its result from a chart read that hit the
   *  read cap — drives the "extraction may be incomplete" indicator. */
  extractionDegraded?: boolean
}

/**
 * Resolve which tool a `tool_end` event refers to. Matches by call_id first
 * (correct under concurrent same-named tools); falls back to name + isRunning
 * only for legacy streams that omit call_id. Returns the index in `tools`, or
 * -1 when nothing matches (so a stale/out-of-order event mutates nothing).
 */
export const matchToolEndIndex = (
  tools: AgentToolCall[],
  eventCallId: string,
  eventToolName: string
): number =>
  tools.findIndex(t =>
    eventCallId ? t.callId === eventCallId : t.name === eventToolName && t.isRunning
  )

export interface CanvasUpdate {
  tab: string
  /** Raw markdown string streamed by the agent (NOT HTML, despite the legacy name). */
  content: string
  version?: number
}

/**
 * Session state for a single agent within a multi-agent workspace.
 */
export interface AgentSession {
  agentId: string
  messages: AgentChatMessage[]
  isStreaming: boolean
  isLoading: boolean
  conversationId: string | null
  error: string | null
  /** Latency/token metadata from the most recent completed turn (display-only). */
  lastTurnMeta?: { latencyMs: number; totalTokens?: number }
}

interface ChatState {
  // Messages & Artifacts
  messages: ChatMessage[]
  artifacts: Artifact[]
  files: UnifiedFile[]
  context: ChatContextType

  // Session State
  sessions: SessionResponse[]
  currentSessionId: string | null
  currentConversationId: string | null
  sessionError: string | null
  isLoadingSession: boolean
  isLoadingSessions: boolean
  shouldRefreshSidebar: boolean

  // UI State
  isPanelOpen: boolean
  isStreaming: boolean
  isGenerating: boolean
  expandedArtifact: ChatMessage | null

  // Multi-Agent Case Panel State
  agentSessions: Record<string, AgentSession>
  activeAgentId: string

  // Patient State
  currentPatient: Patient | null

  // Command State
  isCommandPickerOpen: boolean
  commandPrefix: string

  // User Input
  userInput: string

  // PubMed Grounding State
  researchStatus: ResearchStatus
  currentCitations: PubMedCitation[]

  // Rich Streaming Progress
  streamingProgress: StreamingProgress

  // View State (Project Navigation)
  viewMode: ViewMode
  activeProjectId: string | null
  pendingMessage: { message: string; projectId: string } | null

  // Patient Clinical Data State
  patientData: PatientClinicalData | null
  isPatientDataLoading: boolean
  isPatientHeaderExpanded: boolean
  showPatientDataInput: boolean
  isReconciling: boolean
  patientPanelMode: 'header' | 'column'
}

/**
 * A promise a committed patient-data save could not keep (#444).
 *
 * The write is stored in every case these describe — that is what makes them
 * warnings rather than errors. Rejecting instead would render "Could not save
 * patient data. Please try again." over an edit that IS saved and invite a
 * duplicate submit. Returning them lets the surface that asked for the save
 * tell the clinician what did not happen instead of closing as an unambiguous
 * success. The context reports the facts; the copy belongs to the surface.
 */
export type PatientSaveWarning =
  | 'casey-not-notified'
  | 'panel-not-refreshed'
  | 'audit-not-recorded'
  | 'save-unconfirmed'

/**
 * The follow-through promises only the backend can confirm, read off a save
 * response.
 *
 * Shared by both save legs deliberately: they POST the same route and make the
 * clinician the same promises, but they were written apart and the review that
 * found this feature's defects found them one leg at a time. One reader means a
 * new flag cannot land on the chat leg and quietly miss the workspace leg.
 *
 * `=== false`, never falsy: an absent field is a backend that does not report
 * that promise, which is not the same as the promise being broken.
 */
const followThroughWarnings = (data: unknown): PatientSaveWarning[] => {
  const body = data as { notice_delivered?: boolean; audit_recorded?: boolean } | null | undefined
  const warnings: PatientSaveWarning[] = []
  if (body?.notice_delivered === false) warnings.push('casey-not-notified')
  if (body?.audit_recorded === false) warnings.push('audit-not-recorded')
  return warnings
}

interface ChatActions {
  // Message Management
  addMessage: (message: ChatMessage) => void
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void
  appendToLastMessage: (content: string) => void
  
  // Artifact Management
  addArtifact: (artifact: Artifact) => void
  updateArtifact: (id: string, content: string) => void
  
  // Unified File Management
  addFile: (file: File, source: 'dropzone' | 'paperclip') => Promise<void>
  removeFile: (id: string) => void
  clearFiles: () => void
  transcribeAudio: (audioBlob: Blob) => Promise<string>

  // Context Management
  setContext: (context: ChatContextType | ((prev: ChatContextType) => ChatContextType)) => void
  setPatient: (patient: Patient | null) => void
  
  // Session Management
  loadSession: (sessionId: string) => Promise<void>
  loadSessionForWorkspace: (workspaceId: string, uiStateConversationId?: string) => Promise<void>
  removeWorkspaceConversation: (workspaceId: string) => void
  createNewChat: () => void
  clearSessionError: () => void
  triggerSidebarRefresh: () => void
  renameSession: (sessionId: string, newTitle: string) => Promise<void>
  
  // UI Actions
  togglePanel: () => void
  setStreaming: (isStreaming: boolean) => void
  setGenerating: (isGenerating: boolean) => void
  expandArtifact: (message: ChatMessage | null) => void
  
  // Command Actions
  setCommandPickerOpen: (isOpen: boolean) => void
  setCommandPrefix: (prefix: string) => void
  
  // User Input
  setUserInput: React.Dispatch<React.SetStateAction<string>>
  
  // Chat Operations
  sendMessage: (content: string, overrideProjectId?: string) => Promise<void>
  stopGeneration: () => void
  clearChat: () => void
  retryFromMessage: (messageId: string) => void
  editMessage: (messageId: string) => void
  editingMessageId: string | null

  // Project Navigation
  openProject: (projectId: string) => void
  closeProject: () => void
  startChatInProject: (projectId: string) => void
  startChatInProjectWithMessage: (projectId: string, message: string) => void
  loadSessionFromProjectView: (sessionId: string) => Promise<void>
  setPendingMessage: (message: { message: string; projectId: string } | null) => void

  // Patient Data Actions
  loadPatientData: () => Promise<void>
  setPatientDataDirect: (data: PatientClinicalData | null) => void
  parseAndSavePatientText: (text: string) => Promise<PatientSaveWarning[]>
  parseAndSavePatientTextForWorkspace: (
    workspaceId: string,
    text: string,
  ) => Promise<PatientSaveWarning[]>
  uploadAndSaveCCDA: (file: File) => Promise<void>
  clearPatientData: () => Promise<void>
  togglePatientHeader: () => void
  setShowPatientDataInput: (show: boolean) => void
  reconcilePatientData: () => void
  togglePatientPanelMode: () => void

  // Notification callback (set by WorkspaceManager to pipe SSE events to App-level bell)
  setNotificationCallback: (cb: ((type: 'agent_activity' | 'clinical_alert' | 'content_change', message: string) => void) | null) => void
  // Clinical data refresh callback (set by WorkspaceManager for post-chat patient panel refresh)
  setRefreshClinicalDataCallback: (cb: (() => Promise<void>) | null) => void
  // Artifact refresh callback (set by WorkspaceManager for canvas tab refresh on artifact_update)
  setRefreshArtifactCallback: (cb: ((artifactId: string) => Promise<void>) | null) => void
  // Canvas update callback (set by WorkspaceManager to push tool content to canvas)
  setCanvasUpdateCallback: (cb: ((tab: string, content: string) => void) | null) => void

  // Multi-Agent Case Panel Actions
  sendToAgent: (agentId: string, message: string, mode?: string, canvasSnapshots?: Record<string, string>, options?: { hidden?: boolean; fileIds?: string[]; orchestrate?: boolean }) => Promise<{ output: string; tools: AgentToolCall[] } | null>
  switchAgent: (agentId: string) => void
  getSessionMessages: (agentId: string) => AgentChatMessage[]
  // True while a turn is in flight for this agent. Reads the SAME ref the
  // single-in-flight guard in sendToAgent keys on, so a composer's pre-send check
  // and the guard share one source of truth — they cannot disagree even when the
  // in-flight turn is hidden (isLoading stays false for hidden turns, but the abort
  // controller is set regardless). Prevents handleSend from clearing the composer
  // for a send the guard will refuse.
  isAgentResponding: (agentId: string) => boolean
  initAgentSession: (agentId: string, greeting?: string) => void
  resetAgentSession: (agentId: string) => void
  clearAgentSessions: () => void
  loadAgentSessions: (conversations: Array<{ id: string; agent_id: string; messages: Array<{ id: string; role: string; content: string; created_at: string; metadata?: Record<string, any> }> }>) => void
  setActiveWorkspaceId: (id: string | null) => void
}

const ChatContext = createContext<(ChatState & ChatActions) | undefined>(undefined)

export function ChatProvider({ children }: { children: React.ReactNode }) {
  // MSAL instance and accounts for authentication state
  const { instance: msalInstance, accounts: msalAccounts } = useMsal()

  // Subscription context for upgrade modal
  const { setShowUpgradeModal } = useSubscription()

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [files, setFiles] = useState<UnifiedFile[]>([])
  const [context, setContextState] = useState<ChatContextType>({})
  const [sessions, setSessions] = useState<SessionResponse[]>([])
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null) // Error state for session operations
  const [isLoadingSession, setIsLoadingSession] = useState(false) // Loading state for session operations
  const [isLoadingSessions, setIsLoadingSessions] = useState(false) // Loading state for sessions list - FIXED: start as false
  const [isPanelOpen, setIsPanelOpen] = useState(true)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [expandedArtifact, setExpandedArtifact] = useState<ChatMessage | null>(null)
  const [currentPatient, setCurrentPatient] = useState<Patient | null>(null)
  const [isCommandPickerOpen, setIsCommandPickerOpen] = useState(false)
  const [commandPrefix, setCommandPrefix] = useState('')
  const [userInput, setUserInput] = useState('')
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [shouldRefreshSidebar, setShouldRefreshSidebar] = useState(false)
  const [researchStatus, setResearchStatus] = useState<ResearchStatus>({ active: false, message: '' })
  const [currentCitations, setCurrentCitations] = useState<PubMedCitation[]>([])
  const [streamingProgress, setStreamingProgress] = useState<StreamingProgress>({
    toolCalls: [],
    currentPhase: 'idle',
    thinkingContent: '',
    isThinking: false
  })
  const [viewMode, setViewMode] = useState<ViewMode>('chat')
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const [pendingMessage, setPendingMessage] = useState<{ message: string; projectId: string } | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)

  // Multi-Agent Case Panel State
  const [agentSessions, setAgentSessions] = useState<Record<string, AgentSession>>({})
  const [activeAgentId, setActiveAgentId] = useState<string>('casey')
  const agentAbortControllers = useRef<Record<string, AbortController>>({})
  const activeWorkspaceIdRef = useRef<string | null>(null)

  // Notification callback ref — set by WorkspaceManager to pipe SSE events to App-level bell
  const notificationCallbackRef = useRef<((type: 'agent_activity' | 'clinical_alert' | 'content_change', message: string) => void) | null>(null)

  const setNotificationCallback = useCallback((cb: ((type: 'agent_activity' | 'clinical_alert' | 'content_change', message: string) => void) | null) => {
    notificationCallbackRef.current = cb
  }, [])

  // Clinical data refresh callback — set by WorkspaceManager to trigger granular patient panel refresh
  const refreshClinicalDataRef = useRef<(() => Promise<void>) | null>(null)

  const setRefreshClinicalDataCallback = useCallback((cb: (() => Promise<void>) | null) => {
    refreshClinicalDataRef.current = cb
  }, [])

  // Artifact refresh callback — set by WorkspaceManager to trigger granular canvas tab refresh
  const refreshArtifactRef = useRef<((artifactId: string) => Promise<void>) | null>(null)

  const setRefreshArtifactCallback = useCallback((cb: ((artifactId: string) => Promise<void>) | null) => {
    refreshArtifactRef.current = cb
  }, [])

  // Canvas update callback — set by WorkspaceManager to push content to canvas tabs
  const canvasUpdateRef = useRef<((tab: string, content: string) => void) | null>(null)

  const setCanvasUpdateCallback = useCallback((cb: ((tab: string, content: string) => void) | null) => {
    canvasUpdateRef.current = cb
  }, [])

  // Cleanup agent abort controllers on unmount
  useEffect(() => {
    return () => {
      Object.values(agentAbortControllers.current).forEach(c => c.abort())
      agentAbortControllers.current = {}
    }
  }, [])

  // Workspace-to-conversation mapping (localStorage-backed)
  const getWorkspaceConversationMap = useCallback((): Record<string, string> => {
    try {
      const stored = localStorage.getItem(WS_CONVERSATION_MAP_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (err) { console.warn('[ChatContext] Failed to parse workspace conversation map from localStorage:', err); return {} }
  }, [])

  const setWorkspaceConversation = useCallback((workspaceId: string, conversationId: string) => {
    const map = getWorkspaceConversationMap()
    map[workspaceId] = conversationId
    localStorage.setItem(WS_CONVERSATION_MAP_KEY, JSON.stringify(map))
  }, [getWorkspaceConversationMap])

  const removeWorkspaceConversation = useCallback((workspaceId: string) => {
    const map = getWorkspaceConversationMap()
    delete map[workspaceId]
    localStorage.setItem(WS_CONVERSATION_MAP_KEY, JSON.stringify(map))
  }, [getWorkspaceConversationMap])

  // Patient Clinical Data State
  const [patientData, setPatientData] = useState<PatientClinicalData | null>(null)
  const [isPatientDataLoading, setIsPatientDataLoading] = useState(false)
  const [isPatientHeaderExpanded, setIsPatientHeaderExpanded] = useState(true)
  const [showPatientDataInput, setShowPatientDataInput] = useState(false)
  const [isReconciling, setIsReconciling] = useState(false)
  const [patientPanelMode, setPatientPanelMode] = useState<'header' | 'column'>(() => {
    const stored = localStorage.getItem('caseysPanel.patientPanelMode')
    return stored === 'column' ? 'column' : 'header'
  })


  // Abort controller for stopping generation
  const abortControllerRef = useRef<AbortController | null>(null)

  // Ref for conversation ID to avoid race conditions with async state updates
  // This ensures rapid messages use the same conversation ID
  const conversationIdRef = useRef<string | null>(null)

  // Track whether the conversation has been persisted to the backend
  // Prevents sending locally-generated UUIDs that don't exist in the database
  const isConversationPersistedRef = useRef<boolean>(false)

  // Track if we've already attempted to restore the session on mount
  const hasRestoredSessionRef = useRef<boolean>(false)

  // Refs to access current state in async callbacks without stale closure issues
  const patientDataRef = useRef<PatientClinicalData | null>(null)
  const messagesRef = useRef<ChatMessage[]>(messages)
  const expandedArtifactRef = useRef<ChatMessage | null>(expandedArtifact)
  // Keep refs in sync with state
  useEffect(() => { patientDataRef.current = patientData }, [patientData])
  useEffect(() => { messagesRef.current = messages }, [messages])
  useEffect(() => { expandedArtifactRef.current = expandedArtifact }, [expandedArtifact])

  // AbortController for patient panel crosscheck SSE stream
  const patientPanelCrossCheckAbortRef = useRef<AbortController | null>(null)

  // Cleanup SSE streams on unmount
  useEffect(() => {
    return () => {
      if (patientPanelCrossCheckAbortRef.current) {
        patientPanelCrossCheckAbortRef.current.abort()
      }
    }
  }, [])

  // Message Management
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message])
  }, [])

  const updateMessage = useCallback((id: string, updates: MessageUpdate) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id !== id) return msg
      
      // Handle function updater for entire message
      const resolvedUpdates: any = typeof updates === 'function' ? updates(msg) : updates
      
      // Special handling for content if it's a function updater
      const contentUpdate = resolvedUpdates.content
      if (contentUpdate && typeof contentUpdate === 'function') {
        const contentFn = contentUpdate as (prev: string) => string
        const resolvedContent = contentFn(msg.content)
        // Spread all updates except content, then set resolved content
        const { content: _, ...otherUpdates } = resolvedUpdates
        return {
          ...msg,
          ...otherUpdates,
          content: resolvedContent
        } as ChatMessage
      }
      
      return { ...msg, ...resolvedUpdates } as ChatMessage
    }))
  }, [])

  const appendToLastMessage = useCallback((content: string) => {
    setMessages(prev => {
      if (prev.length === 0) return prev
      const lastIndex = prev.length - 1
      const lastMessage = prev[lastIndex]
      
      return [
        ...prev.slice(0, lastIndex),
        { ...lastMessage, content: lastMessage.content + content }
      ]
    })
  }, [])

  // Artifact Management
  const addArtifact = useCallback((artifact: Artifact) => {
    setArtifacts(prev => {
      // Replace existing artifact of same type or append
      const existingIndex = prev.findIndex(a => a.type === artifact.type)
      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = artifact
        return updated
      }
      return [...prev, artifact]
    })
  }, [])

  const updateArtifact = useCallback((id: string, content: string) => {
    setArtifacts(prev => prev.map(a => 
      a.id === id ? { ...a, content, updatedAt: new Date() } : a
    ))
  }, [])

  // Unified File Management
  const addFile = useCallback(async (file: File, source: 'dropzone' | 'paperclip') => {
    // Use shared validation instead of inline check
    const validation = validateFile(file)
    if (!validation.valid) {
      console.error(`File rejected: ${file.name} — ${validation.error}`)
      return
    }

    const wsId = activeWorkspaceIdRef.current
    if (!wsId) {
      console.error('Cannot upload file — no active workspace')
      return
    }

    const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Add to state with uploading status
    const newFile: UnifiedFile = {
      id: fileId,
      name: file.name,
      type: file.type,
      size: file.size,
      status: 'uploading',
      progress: 10,
      source,
      addedAt: Date.now(),
      rawFile: file
    }
    setFiles(prev => [...prev, newFile])

    try {
      const result = await uploadWorkspaceFile(wsId, file)

      setFiles(prev => prev.map(f =>
        f.id === fileId ? {
          ...f,
          id: result.file_id,
          name: result.filename,
          status: 'ready' as const,
          progress: 100,
          extractedText: result.extracted_text ?? undefined
        } : f
      ))

      // Trigger patient panel crosscheck if text was extracted
      if (result.extracted_text) {
        triggerPatientPanelCrosscheck(result.file_id, result.filename, result.extracted_text)
      }
    } catch (error) {
      console.error('Upload failed:', error)
      setFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, status: 'error' as const, error: 'Upload failed' } : f
      ))
    }
  }, [])

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }, [])

  const clearFiles = useCallback(() => {
    setFiles([])
  }, [])

  /**
   * Trigger patient panel crosscheck via SSE streaming
   * ALL files trigger this to update patient context
   */
  const triggerPatientPanelCrosscheck = useCallback(async (fileId: string, fileName: string, rawText: string) => {
    // Update file to show parsing status
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, parsingStatus: 'pending' as const } : f
    ))

    // SECURITY: Create AbortController for cleanup and cancel any existing request
    const abortController = new AbortController()
    if (patientPanelCrossCheckAbortRef.current) {
      patientPanelCrossCheckAbortRef.current.abort()
    }
    patientPanelCrossCheckAbortRef.current = abortController

    try {
      const projectId = activeProjectId
      const conversationId = isConversationPersistedRef.current
        ? (conversationIdRef.current || currentConversationId)
        : null

      console.log(`📄 Crosschecking file with patient panel: ${fileName}`)

      const token = await getAuthToken()
      if (!token) {
        throw new Error('Not authenticated. Please sign in to parse documents.')
      }
      const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || ''

      const response = await fetch(`${API_BASE_URL}/api/v1/patient-data/parse-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          text: rawText,
          conversation_id: conversationId,
          project_id: projectId
        }),
        signal: abortController.signal  // Add abort signal for cleanup
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) throw new Error('No response body')

      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue

          try {
            const eventData = JSON.parse(line.slice(6))

            if (eventData.event === 'quick_result') {
              // Phase 1 complete - safety-critical data available
              setFiles(prev => prev.map(f =>
                f.id === fileId ? { ...f, parsingStatus: 'quick_done' as const } : f
              ))

              setPatientData({
                id: '',
                patient_summary: eventData.data.patient_summary,
                allergies: eventData.data.allergies,
                code_status: eventData.data.code_status,
                source_description: eventData.data.source_description,
                extractions: [],
                extraction_model: 'gpt-5.4-mini',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })

              // Fix #182: Removed PHI logging — patient_summary contains clinical data
            }

            if (eventData.event === 'complete') {
              // Remove the file from state now that parsing is complete
              // The patient data is now populated, so we don't need the file reference
              setFiles(prev => prev.filter(f => f.id !== fileId))

              const newPatientDataId = eventData.data.id
              setPatientData(prev => ({
                id: newPatientDataId,
                patient_summary: eventData.data.patient_summary,
                allergies: eventData.data.allergies,
                code_status: eventData.data.code_status,
                source_description: prev?.source_description || eventData.data.source_description || '',
                extractions: eventData.data.extractions || [],
                extraction_model: 'gpt-4o',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }))

              console.log(`✅ Patient panel updated from: ${fileName}`)

              // Link to conversation if exists
              const persistedConversationId = conversationIdRef.current
              if (newPatientDataId && persistedConversationId && isConversationPersistedRef.current) {
                try {
                  await api.patch(`/api/v1/patient-data/${newPatientDataId}/link-conversation/${persistedConversationId}`)
                  setPatientData(prev => prev ? { ...prev, conversation_id: persistedConversationId } : null)
                } catch (err) {
                  // Expected if already linked — log at debug level
                  console.debug('[ChatContext] Patient data link (may already exist):', err)
                }
              }
            }

            if (eventData.event === 'progress') {
              // Update extraction phase for progress UI
              const phase = eventData.data.phase as 'extraction' | 'reconciliation' | 'safety_check'
              setFiles(prev => prev.map(f =>
                f.id === fileId ? { ...f, extractionPhase: phase } : f
              ))
              console.log(`🔄 Extraction progress: ${phase} - ${eventData.data.status}`)

              // If Pass 1 extractions are included, update patient data immediately
              // This allows showing detailed extractions while reconciliation runs
              if (eventData.data.extractions && eventData.data.extractions.length > 0) {
                console.log(`📋 Pass 1 extractions received: ${eventData.data.extractions.length} items`)
                setPatientData(prev => ({
                  id: prev?.id || '',
                  patient_summary: eventData.data.patient_summary || prev?.patient_summary || '',
                  allergies: prev?.allergies || [],
                  code_status: prev?.code_status || null,
                  source_description: prev?.source_description || '',
                  extractions: eventData.data.extractions,
                  extraction_model: 'gpt-4o (pass 1)',
                  created_at: prev?.created_at || new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }))
              }
            }

            if (eventData.event === 'error') {
              throw new Error(eventData.data.message)
            }
          } catch (parseError) {
            if (line.trim() && !line.startsWith(':')) {
              console.warn('Failed to parse SSE line:', line, parseError)
            }
          }
        }
      }
    } catch (error) {
      // Ignore abort errors - these are expected during cleanup
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`📄 Crosscheck aborted for: ${fileName}`)
        return
      }
      console.error(`Failed to crosscheck file ${fileName}:`, error)
      // Remove file on error - user can re-upload if needed
      setFiles(prev => prev.filter(f => f.id !== fileId))
    }
  }, [currentConversationId, activeProjectId])

  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string> => {
    const formData = new FormData()
    formData.append('file', audioBlob, 'recording.wav')
    
    const response = await api.post('/api/v1/audio/transcriptions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    
    return response.data.text
  }, [])

  // Context Management
  const setContext = useCallback((newContext: ChatContextType | ((prev: ChatContextType) => ChatContextType)) => {
    if (typeof newContext === 'function') {
      setContextState(newContext)
    } else {
      setContextState(newContext)
    }
  }, [])

  const setPatient = useCallback((patient: Patient | null) => {
    setCurrentPatient(patient)
    if (patient) {
      setContextState(prev => ({
        ...prev,
        patientId: patient.id,
        patientName: patient.name
      }))
    } else {
      // BUGFIX: Clear patient context when switching to default workspace
      setContextState(prev => ({
        ...prev,
        patientId: null,
        patientName: null
      }))
    }
  }, [])

  /**
   * Load a chat session and its messages from the backend.
   * Validates session ownership and converts messages to frontend format.
   * Clears artifacts when switching between sessions.
   * 
   * @param sessionId - UUID of the session to load
   * @throws Sets sessionError state if loading fails (network/permission errors)
   */
  // Ref to track current load operation for cancellation
  const loadSessionAbortRef = useRef<AbortController | null>(null)

  const loadSession = useCallback(async (sessionId: string) => {
    // Skip if already on this session (no need to reload)
    if (sessionId === currentSessionId) {
      return
    }

    // Cancel any previous load operation (handles concurrent loads)
    if (loadSessionAbortRef.current) {
      loadSessionAbortRef.current.abort()
    }
    const abortController = new AbortController()
    loadSessionAbortRef.current = abortController

    setSessionError(null)  // Clear previous errors
    setIsLoadingSession(true)
    try {
      // Fetch conversation messages (artifacts are linked via artifact_id in messages)
      const conversationResponse = await api.get<{conversation: any, messages: BackendMessage[]}>(`/api/conversations/${sessionId}`, {
        signal: abortController.signal
      })

      // Check if operation was cancelled
      if (abortController.signal.aborted) return

      // Convert backend format to frontend format using pure function
      const formattedMessages = conversationResponse.data.messages.map(formatBackendMessage)

      // Messages now include artifact_id directly from the backend (via formatBackendMessage)
      // Fetch artifact content for messages that have linked artifacts
      const messagesWithArtifacts = formattedMessages.filter(m => m.artifact?.artifact_id)
      console.log(`📦 Found ${messagesWithArtifacts.length} messages with linked artifacts`)

      // Fetch artifact details in parallel with abort support
      await Promise.all(messagesWithArtifacts.map(async (message) => {
        if (abortController.signal.aborted) return
        try {
          const artifactId = message.artifact!.artifact_id
          const artifactResponse = await api.get(`/api/v1/artifacts/${artifactId}`, {
            signal: abortController.signal
          })
          const artifact = artifactResponse.data
          message.artifact = {
            type: artifact.type,
            title: artifact.title,
            content: artifact.content,
            artifact_id: artifactId
          }
          console.log(`📎 Loaded artifact "${artifact.title}" for message`)
        } catch (error) {
          // Ignore abort errors, log others
          if (error instanceof Error && error.name !== 'CanceledError') {
            console.warn(`⚠️ Failed to load artifact ${message.artifact?.artifact_id}:`, error)
          }
        }
      }))

      // Final check before state updates (prevents updates on unmounted component)
      if (abortController.signal.aborted) return

      setMessages(formattedMessages)
      setCurrentSessionId(sessionId)
      // Set conversation ID from loaded session (sessionId is the conversation public_id)
      setCurrentConversationId(sessionId)
      conversationIdRef.current = sessionId  // Sync ref for subsequent messages
      isConversationPersistedRef.current = true  // Loaded from DB, definitely persisted
      // Persist to sessionStorage for restore on page refresh
      sessionStorage.setItem('casepanel_last_conversation', sessionId)
      setArtifacts([]) // Clear artifacts when switching sessions
    } catch (error: unknown) {
      // Ignore abort errors (expected on cleanup/navigation)
      if (error instanceof Error && error.name === 'CanceledError') return

      // Provide specific error messages based on error type
      let errorMsg = 'Failed to load session. Please try again.'

      if (error instanceof Error) {
        // Check for specific HTTP errors if using Axios
        const axiosError = error as any
        const status = axiosError?.response?.status

        errorMsg = status === 404
          ? 'Session not found.'
          : status === 403
          ? 'Access denied to this session.'
          : 'Failed to load session. Please try again.'
      }

      setSessionError(errorMsg)
      console.error('Failed to load session:', error)
    } finally {
      setIsLoadingSession(false)
    }
  }, [currentSessionId])

  // Sync workspace-conversation mapping whenever conversationId changes
  useEffect(() => {
    if (currentConversationId && activeWorkspaceIdRef.current) {
      setWorkspaceConversation(activeWorkspaceIdRef.current, currentConversationId)
      // Also persist to workspace ui_state for cross-device/session recovery
      workspaceService.update(activeWorkspaceIdRef.current, {
        ui_state: { conversation_id: currentConversationId }
      }).catch((err) => { console.warn('[ChatContext] Failed to persist conversation_id to workspace ui_state:', err) })
    }
  }, [currentConversationId, setWorkspaceConversation])

  /**
   * Load the correct chat session for a given workspace.
   * Uses the localStorage workspace-conversation map to find the right conversation.
   * If no mapping exists (fresh workspace), clears messages for a blank chat.
   */
  const loadSessionForWorkspace = useCallback(async (workspaceId: string, uiStateConversationId?: string) => {
    const map = getWorkspaceConversationMap()
    const conversationId = map[workspaceId] || uiStateConversationId
    if (conversationId) {
      // Ensure localStorage is in sync
      if (!map[workspaceId] && conversationId) {
        setWorkspaceConversation(workspaceId, conversationId)
      }
      await loadSession(conversationId)
    } else {
      // Fresh chat workspace — clear messages for a blank slate
      setMessages([])
      setCurrentSessionId(null)
      setCurrentConversationId(null)
      conversationIdRef.current = null
      isConversationPersistedRef.current = false
    }
  }, [getWorkspaceConversationMap, loadSession, setWorkspaceConversation])

  /**
   * Create a new chat session.
   * Clears all state. Backend will create new session on first message.
   */
  const createNewChat = useCallback(() => {
    setMessages([])
    setArtifacts([])
    setCurrentSessionId(null)  // Backend will create new session on first message
    setCurrentConversationId(null)  // Clear conversation ID for new chat
    conversationIdRef.current = null  // Clear ref for new conversation
    isConversationPersistedRef.current = false  // New chat is not persisted yet
    setContextState({})
    setCurrentPatient(null)
    setPatientData(null)  // Clear patient data when starting new chat
    setSessionError(null)  // Clear any errors
    // Clear persisted conversation so refresh doesn't restore old conversation
    sessionStorage.removeItem('casepanel_last_conversation')
  }, [])

  /**
   * Clear session error message.
   */
  const clearSessionError = useCallback(() => {
    setSessionError(null)
  }, [])

  /**
   * Trigger sidebar refresh (reload conversations list)
   */
  const triggerSidebarRefresh = useCallback(() => {
    setShouldRefreshSidebar(prev => !prev)  // Toggle to trigger useEffect
  }, [])

  /**
   * Rename a chat session.
   * Updates local state optimistically and syncs with backend.
   */
  const renameSession = useCallback(async (sessionId: string, newTitle: string) => {
    // Optimistic update - update local state immediately
    setSessions(prev => prev.map(session =>
      session.id === sessionId
        ? { ...session, title: newTitle }
        : session
    ))

    try {
      await api.patch(`/api/conversations/${sessionId}`, { title: newTitle })
    } catch (error) {
      console.error('Failed to rename session:', error)
      // Revert optimistic update on failure by refreshing
      triggerSidebarRefresh()
    }
  }, [triggerSidebarRefresh])

  // UI Actions
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev)
  }, [])

  const setStreaming = useCallback((streaming: boolean) => {
    setIsStreaming(streaming)
  }, [])

  const setGenerating = useCallback((generating: boolean) => {
    setIsGenerating(generating)
  }, [])

  const expandArtifact = useCallback(async (message: ChatMessage | null) => {
    console.log('🔍 expandArtifact called', {
      hasMessage: !!message,
      hasArtifact: !!message?.artifact,
      artifactType: message?.artifact?.type,
      hasContent: !!message?.artifact?.content,
      contentLength: message?.artifact?.content?.length,
      artifactId: message?.artifact?.artifact_id
    })

    if (!message) {
      setExpandedArtifact(null)
      return
    }

    // If artifact has content, use it directly
    if (message.artifact?.content) {
      console.log('✅ Setting expandedArtifact with content')
      setExpandedArtifact(message)
      setIsPanelOpen(true)
      return
    }

    // If artifact has ID but no content (loaded from session), fetch from API
    if (message.artifact?.artifact_id) {
      try {
        const response = await api.get(`/api/v1/artifacts/${message.artifact.artifact_id}`)
        const artifactData = response.data

        // Update message with fetched content
        const updatedMessage: ChatMessage = {
          ...message,
          artifact: {
            ...message.artifact,
            title: artifactData.title || message.artifact.title,
            content: artifactData.content,
            type: artifactData.type || message.artifact.type
          }
        }

        // Update the message in state so it persists
        setMessages(prev => prev.map(m =>
          m.id === message.id ? updatedMessage : m
        ))

        setExpandedArtifact(updatedMessage)
        setIsPanelOpen(true)
        console.log(`✅ Loaded artifact content: ${message.artifact.artifact_id}`)
      } catch (error) {
        console.error('❌ Failed to load artifact:', error)
        // Still show the message, but with empty content
        setExpandedArtifact(message)
        setIsPanelOpen(true)
      }
    } else {
      // No artifact_id, just show what we have
      setExpandedArtifact(message)
      setIsPanelOpen(true)
    }
  }, [])

  const setCommandPickerOpen = useCallback((isOpen: boolean) => {
    setIsCommandPickerOpen(isOpen)
  }, [])

  const setCommandPrefixState = useCallback((prefix: string) => {
    setCommandPrefix(prefix)
  }, [])

  // Project Navigation
  const openProject = useCallback((projectId: string) => {
    setActiveProjectId(projectId)
    setViewMode('project-detail')
  }, [])

  const closeProject = useCallback(() => {
    setActiveProjectId(null)
    setViewMode('chat')
  }, [])

  const startChatInProject = useCallback((projectId: string) => {
    // Set active project and switch to chat view
    setActiveProjectId(projectId)
    setViewMode('chat')
    // Clear existing chat state for new chat
    setMessages([])
    setArtifacts([])
    setCurrentSessionId(null)
    setCurrentConversationId(null)
    conversationIdRef.current = null  // Clear ref for new conversation
    isConversationPersistedRef.current = false  // New chat is not persisted yet
    setContextState({})
    setCurrentPatient(null)
    setSessionError(null)
    // Clear persisted conversation
    sessionStorage.removeItem('casepanel_last_conversation')
  }, [])

  const startChatInProjectWithMessage = useCallback((projectId: string, message: string) => {
    // Set active project and switch to chat view
    setActiveProjectId(projectId)
    setViewMode('chat')
    // Clear existing chat state for new chat
    setMessages([])
    setArtifacts([])
    setCurrentSessionId(null)
    setCurrentConversationId(null)
    conversationIdRef.current = null  // Clear ref for new conversation
    isConversationPersistedRef.current = false  // New chat is not persisted yet
    setContextState({})
    setCurrentPatient(null)
    setSessionError(null)
    // Clear persisted conversation
    sessionStorage.removeItem('casepanel_last_conversation')
    // Store message AND projectId together to avoid closure issues
    setPendingMessage({ message, projectId })
  }, [])

  const loadSessionFromProjectView = useCallback(async (sessionId: string) => {
    // Clear project state and switch to chat view first
    // This ensures user sees ChatUI (with loading state) immediately
    setActiveProjectId(null)
    setViewMode('chat')

    // Now load the session - ChatUI will handle loading state
    await loadSession(sessionId)
  }, [loadSession])

  // ============================================================================
  // Session Persistence (Restore conversation on page refresh)
  // ============================================================================

  /**
   * Persist current conversation ID to sessionStorage for restore on refresh.
   * Only persist if conversation has been confirmed by backend.
   */
  useEffect(() => {
    if (currentConversationId && isConversationPersistedRef.current) {
      sessionStorage.setItem('casepanel_last_conversation', currentConversationId)
      console.log(`💾 Persisted conversation ID: ${currentConversationId}`)
    }
  }, [currentConversationId])

  /**
   * Restore last conversation on mount (page refresh).
   * This ensures patient data and messages are restored.
   */
  useEffect(() => {
    // Only run once on mount
    if (hasRestoredSessionRef.current) return
    hasRestoredSessionRef.current = true

    const savedConversationId = sessionStorage.getItem('casepanel_last_conversation')
    if (savedConversationId) {
      console.log(`🔄 Restoring conversation from sessionStorage: ${savedConversationId}`)
      // loadSession will set currentConversationId and isConversationPersistedRef
      // which will trigger loadPatientData via the existing useEffect
      loadSession(savedConversationId).catch((error) => {
        console.warn('Failed to restore conversation:', error)
        // Clear invalid saved conversation
        sessionStorage.removeItem('casepanel_last_conversation')
      })
    }
  }, [loadSession])

  // ============================================================================
  // Patient Clinical Data Functions
  // ============================================================================

  /**
   * Load patient data for the current conversation or project
   */
  // H1 FIX: AbortController ref for cancelling in-flight patient data requests
  const patientDataAbortRef = useRef<AbortController | null>(null)

  const loadPatientData = useCallback(async () => {
    const conversationId = conversationIdRef.current || currentConversationId
    const projectId = activeProjectId

    // H2 FIX: Only use conversation ID if it's been confirmed by the backend
    // This prevents 500 errors from querying non-existent conversations
    const canUseConversationId = conversationId && isConversationPersistedRef.current

    if (!canUseConversationId && !projectId) {
      // No valid context to query - don't set patientData to null here
      // as we may be in the middle of extraction with user-scoped data
      return
    }

    // H1 FIX: Cancel any in-flight request to prevent race conditions
    if (patientDataAbortRef.current) {
      patientDataAbortRef.current.abort()
    }
    const abortController = new AbortController()
    patientDataAbortRef.current = abortController

    setIsPatientDataLoading(true)
    try {
      const endpoint = canUseConversationId
        ? `/api/v1/patient-data/context/${conversationId}`
        : `/api/v1/patient-data/context/project/${projectId}`

      const response = await api.get(endpoint, {
        signal: abortController.signal
      })

      // H1 FIX: Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        // API returns array, take first element if exists
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setPatientData(response.data[0])
          console.log('🏥 Loaded patient clinical data')
        } else {
          // Don't clear patient data if we have quick extraction data (id='')
          // Quick extraction sets patientData with id='' before full extraction saves to DB
          // If we clear here, we lose the quick extraction data while full extraction is running
          setPatientData(prev => {
            if (prev && prev.id === '') {
              console.log('🏥 Preserving quick extraction data (DB has no data yet)')
              return prev
            }
            return null
          })
        }
      }
    } catch (error: unknown) {
      // Ignore aborted requests
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        return
      }
      // 404 is expected when no patient data exists
      // 500 can happen if conversation doesn't exist yet (race condition) - also treat as no data
      if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 500)) {
        // Don't clear existing patient data on error - user may have just uploaded it
        // Only log if not a known expected condition
        if (error.response?.status === 500) {
          console.warn('Patient data API returned 500 - conversation may not exist yet')
        }
      } else {
        console.error('Failed to load patient data:', error)
      }
    } finally {
      // H1 FIX: Only clear loading state if not aborted
      if (!abortController.signal.aborted) {
        setIsPatientDataLoading(false)
      }
    }
  }, [currentConversationId, activeProjectId])

  /**
   * Parse EHR text and save patient data
   * Only sends conversation_id if it has been persisted to the backend
   * User-scoped patient data (no conversation/project) is allowed and will be linked later
   */
  const parseAndSavePatientText = useCallback(async (text: string): Promise<PatientSaveWarning[]> => {
    const projectId = activeProjectId

    // Only use conversation_id if it's been confirmed by the backend
    // This prevents sending locally-generated UUIDs that don't exist in the database
    const conversationId = isConversationPersistedRef.current
      ? (conversationIdRef.current || currentConversationId)
      : null

    // User-scoped patient data is allowed — will be linked to conversation when created
    console.debug(`[ChatContext] Saving patient data (conversation: ${conversationId || 'user-scoped'}, project: ${projectId || 'none'})`)

    setIsPatientDataLoading(true)
    try {
      // Clinical extraction can take 60-120 seconds for long documents
      // NOTE (#475): this sends conversation_id AND project_id, and the backend
      // refuses a request naming more than one save scope (400). Which ids this
      // leg should send is #475's scope, deliberately not changed here — #458
      // only makes the resulting refusal readable to the clinician instead of a
      // generic "please try again".
      const response = await api.post('/api/v1/patient-data/parse', {
        text,
        conversation_id: conversationId,
        project_id: projectId
      }, { timeout: 180000 })  // 3 minute timeout for AI extraction

      const warnings: PatientSaveWarning[] = []

      if (response.data) {
        // MERGE, never replace. The response is a PatientDataResponse — the
        // fields the parse produced — while the panel renders a
        // PatientClinicalData, which also carries panel_content, the structured
        // sections (vitals/labs/imaging/orders/medications), med_recon_status and
        // allergy_conflicts. None of those are in the response because the write
        // does not touch them, so assigning the response wholesale did not
        // "update" the panel: it deleted every section the response has no field
        // for, and the panel fell back to the extractions until the next reload.
        // Keyed on the row id so this only ever merges the SAME row: the upsert
        // branches return the row that was already on screen, while a user-scoped
        // save creates a different row whose values must not inherit the old
        // one's sections.
        setPatientData(prev =>
          prev && prev.id === response.data.id
            ? { ...prev, ...response.data }
            : response.data
        )
        console.log('🏥 Parsed and saved patient data')
      } else {
        // An empty 200 (a proxy that strips the body, a future response-shape
        // change). The panel was never updated and the flags below all read as
        // "fine", so without this the dialog closed on an unambiguous success
        // with zero evidence anything happened. Same promise the workspace leg
        // makes: say what could not be confirmed rather than assuming it.
        console.warn('[ChatContext] Patient-data save returned no body — nothing could be confirmed')
        warnings.push('save-unconfirmed')
      }

      // #444: the save promises a write AND a notify, and only the backend
      // knows whether the notify landed. Closing the dialog is deliberately NOT
      // done here any more: the dialog decides, because a save that could not
      // notify must stay on screen long enough to say so.
      warnings.push(...followThroughWarnings(response?.data))
      return warnings
    } catch (error) {
      console.error('Failed to parse patient data:', describeSaveError(error))
      throw error
    } finally {
      setIsPatientDataLoading(false)
    }
  }, [currentConversationId, activeProjectId])

  /**
   * Parse EHR text and save patient data scoped to a workspace (#444).
   *
   * The admission/flow edit path: those workspaces have no chat conversation,
   * and their panel hydrates from the workspace-scoped clinical-data row, so
   * the save is keyed on the workspace and the panel is re-hydrated afterwards
   * (via the WorkspaceManager-registered refresh callback). The backend
   * parses, saves, and notifies Casey.
   *
   * What the re-hydrate can actually show is narrower than "the edit sticks":
   * the writer sets patient_summary, allergies, code_status and extractions,
   * and `fromExtractions` resolves each panel section as
   * `structured ?? panel_content ?? extractions`, so on a post-intake
   * workspace the intake-written sections outrank anything this save
   * produces. PatientDataInputDialog's copy is worded to match.
   */
  const parseAndSavePatientTextForWorkspace = useCallback(async (
    workspaceId: string,
    text: string,
  ): Promise<PatientSaveWarning[]> => {
    setIsPatientDataLoading(true)
    try {
      // Clinical extraction can take 60-120 seconds for long documents
      const response = await api.post('/api/v1/patient-data/parse', {
        text,
        workspace_id: workspaceId
      }, { timeout: 180000 })  // 3 minute timeout for AI extraction

      // Same window as the notice: the clinical write committed before the
      // HIPAA audit/PHI rows were attempted, so a bookkeeping failure arrives as
      // a warning on a 200 rather than an error over a stored edit.
      const warnings: PatientSaveWarning[] = []

      if (!response?.data) {
        // An empty 200 (a proxy that strips the body, a future response-shape
        // change). followThroughWarnings reads `=== false`, never falsy, so an
        // absent body produces NO warnings and this leg closed the dialog on an
        // unambiguous success with zero evidence the write landed or that Casey
        // was told. The chat leg has always guarded this and its comment claims
        // the two legs make the same promise — which only became true here.
        // This is the PRIMARY #444 path (admission/flow), so 'save-unconfirmed'
        // was unreachable exactly where it matters most.
        console.warn('[ChatContext] Workspace patient-data save returned no body — nothing could be confirmed')
        warnings.push('save-unconfirmed')
      }
      warnings.push(...followThroughWarnings(response?.data))

      // Re-hydrate the workspace clinical data — the panel renders from it,
      // not from this response body. Isolated from the save's failure path
      // (same as the two SSE-completion refreshes below): the POST has already
      // committed, so letting a refresh blip reject would tell the clinician
      // "Could not save patient data. Please try again." about an edit that is
      // already stored, and they would re-submit it. Reported as a warning
      // instead, because a panel still showing pre-edit allergies while the
      // dialog closes on "saved" is exactly the confusion this feature exists
      // to remove.
      const refresh = refreshClinicalDataRef.current
      if (!refresh) {
        // WorkspaceManager nulls this on cleanup, so it can genuinely be absent.
        console.warn('[ChatContext] Saved workspace patient data, but no clinical-data refresh is registered — the panel will show stale data until it reloads')
        warnings.push('panel-not-refreshed')
      } else {
        try {
          await refresh()
        } catch (err) {
          console.error('[ChatContext] Saved workspace patient data, but the panel refresh failed:', describeSaveError(err))
          warnings.push('panel-not-refreshed')
        }
      }
      console.log('🏥 Parsed and saved workspace patient data')
      return warnings
    } catch (error) {
      console.error('Failed to parse workspace patient data:', describeSaveError(error))
      throw error
    } finally {
      setIsPatientDataLoading(false)
    }
  }, [])

  /**
   * Upload and parse CCDA file
   * Only sends conversation_id if it has been persisted to the backend
   * User-scoped patient data (no conversation/project) is allowed and will be linked later
   */
  const uploadAndSaveCCDA = useCallback(async (file: File) => {
    const projectId = activeProjectId

    // Only use conversation_id if it's been confirmed by the backend
    // This prevents sending locally-generated UUIDs that don't exist in the database
    const conversationId = isConversationPersistedRef.current
      ? (conversationIdRef.current || currentConversationId)
      : null

    // User-scoped patient data is allowed - will be linked to conversation when created
    console.log(`📋 Uploading CCDA (conversation: ${conversationId || 'user-scoped'}, project: ${projectId || 'none'})`)

    setIsPatientDataLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (conversationId) formData.append('conversation_id', conversationId)
      if (projectId) formData.append('project_id', projectId)

      // Clinical extraction can take 60-120 seconds for large files
      const response = await api.post('/api/v1/patient-data/upload-ccda', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000  // 3 minute timeout for AI extraction
      })

      if (response.data) {
        setPatientData(response.data)
        setShowPatientDataInput(false)
        console.log('🏥 Uploaded and parsed CCDA file')
      }
    } catch (error) {
      console.error('Failed to upload CCDA:', error)
      throw error
    } finally {
      setIsPatientDataLoading(false)
    }
  }, [currentConversationId, activeProjectId])

  /**
   * Clear patient data from context
   */
  const clearPatientData = useCallback(async () => {
    if (!patientData?.id) {
      setPatientData(null)
      return
    }

    try {
      await api.delete(`/api/v1/patient-data/${patientData.id}`)
      setPatientData(null)
      console.log('🏥 Cleared patient data')
    } catch (error) {
      console.error('Failed to clear patient data:', error)
      throw error
    }
  }, [patientData?.id])

  /**
   * Toggle patient header expanded/collapsed
   */
  const togglePatientHeader = useCallback(() => {
    setIsPatientHeaderExpanded(prev => !prev)
  }, [])

  const togglePatientPanelMode = useCallback(() => {
    setPatientPanelMode(prev => {
      const next = prev === 'header' ? 'column' : 'header'
      localStorage.setItem('caseysPanel.patientPanelMode', next)
      return next
    })
  }, [])

  // Auto-load patient data when conversation or project changes
  // H1 FIX: Cleanup AbortController on unmount or dependency change
  // H2 FIX: Only load patient data if conversation is persisted (avoids race condition
  // where client-generated UUID triggers API call before backend creates conversation)
  useEffect(() => {
    // Only load patient data when:
    // 1. We have a persisted conversation ID (backend confirmed), OR
    // 2. We have a project ID (projects are always persisted)
    // This prevents 500 errors from querying non-existent conversations
    const hasPersistedConversation = currentConversationId && isConversationPersistedRef.current
    const hasProject = !!activeProjectId

    if (hasPersistedConversation || hasProject) {
      loadPatientData()
    } else if (!currentConversationId && !activeProjectId) {
      // Only clear patient data if we have no context at all
      // (don't clear during new chat creation while extraction is in progress)
      setPatientData(null)
    }

    // Cleanup: abort in-flight request when dependencies change or unmount
    return () => {
      if (patientDataAbortRef.current) {
        patientDataAbortRef.current.abort()
      }
    }
  }, [currentConversationId, activeProjectId, loadPatientData])

  // Task 3.3: Sync frontend when AI model updates patient data via tools
  // When streaming shows update_patient_data tool was called, refetch data
  // FIX: Track by tool call ID, not array length - status changes don't change count
  const processedPatientUpdateCallsRef = useRef<Set<string>>(new Set())
  // Ref to track current toolCalls for persisting to message metadata (avoids stale closure)
  const toolCallsRef = useRef<ToolCallStatus[]>([])
  // Ref to track pending file citations during streaming (attached to message on completion)
  const pendingFileCitationsRef = useRef<FileCitation[]>([])
  useEffect(() => {
    const toolCalls = streamingProgress.toolCalls || []

    // Keep ref in sync for use in finally block (avoids stale closure)
    toolCallsRef.current = toolCalls

    // Find any update_patient_data calls that are complete but haven't been processed
    // This correctly detects status changes (running → complete), not just new additions
    const completedPatientUpdates = toolCalls.filter(
      t => t.toolName === 'update_patient_data' &&
           t.status === 'complete' &&
           !processedPatientUpdateCallsRef.current.has(t.id)
    )

    if (completedPatientUpdates.length > 0) {
      console.log('🔄 AI updated patient data, refreshing...')
      // Mark these as processed to avoid duplicate refreshes
      completedPatientUpdates.forEach(t => processedPatientUpdateCallsRef.current.add(t.id))
      loadPatientData()
    }
  }, [streamingProgress.toolCalls, loadPatientData])

  // Reset processed calls when streaming completes (conversation turn ends)
  useEffect(() => {
    if (streamingProgress.currentPhase === 'idle') {
      processedPatientUpdateCallsRef.current.clear()
    }
  }, [streamingProgress.currentPhase])

  // Chat Operations
  // overrideProjectId allows passing project_id directly to avoid React closure issues
  const sendMessage = useCallback(async (content: string, overrideProjectId?: string) => {
    if (!content.trim()) return

    // Handle edit mode: remove edited message and all subsequent messages
    if (editingMessageId) {
      setMessages(prev => {
        const editIndex = prev.findIndex(m => m.id === editingMessageId)
        return editIndex !== -1 ? prev.slice(0, editIndex) : prev
      })
      setEditingMessageId(null)
    }

    // Capture attachment metadata BEFORE clearing files
    const messageAttachments = files.length > 0 ? files.map(f => ({
      id: f.id,
      name: f.name,
      type: f.type,
      size: f.size,
      blobUrl: f.blobUrl,
      source: f.source
    })) : undefined

    // Add user message with attachments
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString(),
      attachments: messageAttachments
    }
    addMessage(userMessage)

    // Create assistant message placeholder for streaming
    const assistantMessage: ChatMessage = {
      id: `msg-${Date.now() + 1}`,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString()
    }
    addMessage(assistantMessage)

    // Create new abort controller
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsGenerating(true)
    setIsStreaming(true)
    setResearchStatus({ active: false, message: '' })
    setCurrentCitations([])
    // Reset streaming progress for new message
    setStreamingProgress({
      toolCalls: [],
      currentPhase: 'generating',
      thinkingContent: '',
      isThinking: false
    })

    const startTime = Date.now()

    try {
      // Generate conversation ID upfront if this is a new conversation
      // Needed for file uploads before message send
      let effectiveConversationId = conversationIdRef.current
      if (!effectiveConversationId) {
        effectiveConversationId = crypto.randomUUID()
        conversationIdRef.current = effectiveConversationId
        setCurrentConversationId(effectiveConversationId)
        console.log(`🆕 Generated client-side conversation ID: ${effectiveConversationId}`)
      }

      // Collect file IDs (already uploaded to workspace via addFile)
      const fileIds = files.filter(f => f.status === 'ready').map(f => f.id)
      if (files.length > 0) {
        setFiles([])
        console.log('📄 Cleared file attachments from input state')
      }

      // Build message history for context
      // Fix #232: Use messagesRef to avoid stale closure on rapid successive sends
      const messageHistory = [
        ...messagesRef.current.map(m => ({
          role: m.role,
          content: m.content
        })),
        { role: 'user', content }
      ]

      // Use backend adapter for streaming
      // Use overrideProjectId if provided (from pending message), otherwise use activeProjectId
      const effectiveProjectId = overrideProjectId ?? activeProjectId

      const streamParams = {
        messages: messageHistory,
        context: {
          ...context,
          conversation_id: effectiveConversationId,
          project_id: effectiveProjectId,
          workspace_id: activeWorkspaceIdRef.current,
          file_ids: fileIds.length > 0 ? fileIds : undefined,
          // Include patient data directly so backend doesn't need DB lookup
          // Only send if we have a valid ID (not empty from quick_result)
          // If ID is empty, backend will fall back to DB lookup by conversation_id
          patient_data: patientData?.id ? {
            id: patientData.id,
            patient_summary: patientData.patient_summary,
            allergies: patientData.allergies,
            code_status: patientData.code_status,
            source_description: patientData.source_description,
            extractions: patientData.extractions,
            extraction_model: patientData.extraction_model
          } : null
        },
        // Include attachment metadata for persistence to message metadata
        attachments: messageAttachments
      }

      let tokenCount = 0

      // Reasoning-phase accumulation, mirroring the case-panel sendToAgent path:
      // a buffer joins phases with THINKING_PHASE_SEPARATOR; base marks each phase
      // start so finalizeThinkingPhase handles delta-vs-delta-less providers.
      let thinkingBuffer = ''
      let thinkingPhaseBase = ''
      let thinkingStartTime = 0

      for await (const event of backendAdapter.streamChat(streamParams)) {
        // Check if aborted
        if (abortController.signal.aborted) {
          updateMessage(assistantMessage.id, {
            content: (prev: string) => prev + '\n\n_[Generation stopped by user]_'
          })
          break
        }

        // PHASE 2.2: Capture session_id from backend SSE
        if (event.event === 'session') {
          setCurrentSessionId(event.data?.session_id)
          continue
        }

        // Capture conversation_id from backend SSE
        if (event.event === 'conversation') {
          const conversationId = event.data?.conversation_id
          if (conversationId) {
            setCurrentConversationId(conversationId)
            conversationIdRef.current = conversationId
            isConversationPersistedRef.current = true  // Backend confirmed - now persisted
            // Persist to sessionStorage for restore on page refresh
            sessionStorage.setItem('casepanel_last_conversation', conversationId)
            triggerSidebarRefresh()  // Refresh sidebar to show new conversation
            console.log(`✅ Conversation ID captured and persisted: ${conversationId}`)

            // Link user-scoped patient data to the new conversation
            // Use ref to access current patientData (avoids stale closure from callback creation)
            const currentPatientData = patientDataRef.current
            if (currentPatientData?.id && !currentPatientData?.conversation_id) {
              try {
                await api.patch(`/api/v1/patient-data/${currentPatientData.id}/link-conversation/${conversationId}`)
                // Update local state to reflect the link
                setPatientData(prev => prev ? { ...prev, conversation_id: conversationId } : null)
                console.log(`🔗 Linked patient data ${currentPatientData.id} to conversation ${conversationId}`)
              } catch (linkError) {
                console.warn('Failed to link patient data to conversation:', linkError)
              }
            }

            // H2 FIX: Now that conversation is persisted, load any existing patient data
            // This handles the case where patient data was uploaded to a different conversation
            // and needs to be fetched for this new conversation
            loadPatientData()
          }
          continue
        }

        // Handle thinking_start event - LLM reasoning begins
        // Begin a new phase in the local buffer (separator prepended after the first).
        if (event.event === 'thinking_start') {
          thinkingPhaseBase = beginThinkingPhase(thinkingBuffer)
          thinkingBuffer = thinkingPhaseBase
          thinkingStartTime = Date.now()
          setStreamingProgress(prev => ({
            ...prev,
            currentPhase: 'thinking',
            isThinking: true,
            thinkingContent: thinkingBuffer
          }))
          continue
        }

        // Handle thinking_delta event - Stream thinking content
        if (event.event === 'thinking_delta') {
          thinkingBuffer += event.data?.content || ''
          setStreamingProgress(prev => ({
            ...prev,
            thinkingContent: thinkingBuffer
          }))
          continue
        }

        // Handle thinking_complete event - LLM reasoning done
        if (event.event === 'thinking_complete') {
          thinkingBuffer = finalizeThinkingPhase(thinkingBuffer, thinkingPhaseBase, event.data?.content || '')
          // Prefer the provider-reported duration so live matches the persisted value
          // (the backend persists the SUM of provider duration_ms); fall back to wall-clock.
          const providerMs = Number(event.data?.duration_ms) || 0
          const phaseMs = providerMs > 0 ? providerMs : (thinkingStartTime ? Date.now() - thinkingStartTime : 0)

          // Store the full joined buffer on the streaming assistant message, targeting it by
          // its stable id (not array position) so a message appended after it — e.g. an
          // artifact bubble — can't shift the index and silently drop the reasoning phase.
          setMessages(msgs => {
            if (!msgs.some(m => m.id === assistantMessage.id)) {
              console.error('thinking_complete: streaming assistant message not found — reasoning will be lost', { assistantMessageId: assistantMessage.id })
              return msgs
            }
            return msgs.map(msg =>
              msg.id === assistantMessage.id
                ? {
                    ...msg,
                    thinkingContent: thinkingBuffer,
                    thinkingDurationMs: (msg.thinkingDurationMs || 0) + phaseMs
                  }
                : msg
            )
          })

          setStreamingProgress(prev => ({
            ...prev,
            currentPhase: 'generating',
            isThinking: false
          }))
          continue
        }

        // Handle skill_loaded event - skills auto-loaded based on user message
        if (event.event === 'skill_loaded') {
          const skillName = event.data?.skill_name || 'unknown'
          const displayName = `Loaded Skill: ${skillName}`

          setStreamingProgress(prev => ({
            ...prev,
            toolCalls: [...prev.toolCalls, {
              id: `skill-${skillName}`,
              toolName: 'load_skill',
              displayName,
              status: 'complete',  // Already loaded
              args: { skill_name: skillName },
              startedAt: Date.now()
            }]
          }))
          console.log(`📖 Skill loaded: ${skillName}`)
          continue
        }

        // Handle tool_start event for rich streaming UX
        if (event.event === 'tool_start') {
          const toolName = event.data?.tool_name || 'unknown'
          const displayName = formatToolDisplayName(toolName)

          setStreamingProgress(prev => ({
            ...prev,
            currentPhase: 'searching',
            toolCalls: [...prev.toolCalls, {
              id: event.data?.call_id || crypto.randomUUID(),
              toolName,
              displayName,
              status: 'running',
              args: event.data?.args,
              startedAt: Date.now()
            }]
          }))
          // Also set legacy research status for backwards compatibility
          setResearchStatus({ active: true, message: `${displayName}...` })
          continue
        }

        // Handle tool_end event for rich streaming UX
        if (event.event === 'tool_end') {
          const callId = event.data?.call_id
          setStreamingProgress(prev => ({
            ...prev,
            toolCalls: prev.toolCalls.map(tc =>
              tc.id === callId
                ? {
                    ...tc,
                    status: event.data?.success ? 'complete' : 'error',
                    citationCount: event.data?.citation_count || 0,
                    resultSummary: event.data?.result_summary
                  }
                : tc
            )
          }))
          // Fire notification for tool completions
          const toolName = event.data?.tool_name as string | undefined
          if (toolName) {
            const isClinical = ['drug_interaction_check', 'gap_analysis', 'search_clinical_evidence'].includes(toolName)
            notificationCallbackRef.current?.(
              isClinical ? 'clinical_alert' : 'agent_activity',
              `${formatToolDisplayName(toolName)} completed`
            )
          }
          continue
        }

        // Handle research status events (PubMed grounding) - legacy
        if (event.event === 'status' && event.data?.type === 'research') {
          setResearchStatus({ active: true, message: event.data.message || 'Researching...' })
          continue
        }

        // Handle citations from PubMed tool calls
        if (event.event === 'citations') {
          const citations = event.data?.citations || []
          setCurrentCitations(citations)
          setResearchStatus({ active: false, message: '' })
          console.log(`📚 Received ${citations.length} citations`)
          continue
        }

        // Handle web search events (built-in Azure OpenAI web search)
        if (event.event === 'web_search') {
          const status = event.data?.status || 'searching'
          console.log(`🔍 Guidelines search: ${status}`)
          setResearchStatus({ active: true, message: 'Searching Guidelines...' })
          continue
        }

        // Handle web citations from Azure OpenAI web search
        if (event.event === 'web_citations') {
          const citations = event.data?.citations || []
          console.log(`🌐 Web citations: ${citations.length} sources`)
          // Web citations are already embedded in the response text with URL annotations
          // Just clear the research status
          setResearchStatus({ active: false, message: '' })
          continue
        }

        // Handle file citations from uploaded documents (PDFs, clinical notes, etc.)
        if (event.event === 'file_citations') {
          const citations = event.data?.citations || []
          console.log(`📄 File citations: ${citations.length} sources`)
          // Store pending citations to attach to message when complete
          pendingFileCitationsRef.current = citations.map((c: any) => ({
            filename: c.filename,
            quote: c.quote,
            index: c.index,  // Single insertion point (Azure Responses API)
            fileId: c.file_id,
          }))
          continue
        }

        // Handle subscription required event (trial expired or no subscription)
        if (event.event === 'subscription_required') {
          console.log('🚫 Subscription required:', event.data?.message)
          // Show upgrade modal
          setShowUpgradeModal(true)
          // Update the assistant message with the subscription message
          updateMessage(assistantMessage.id, {
            content: event.data?.message || 'Your trial has ended. Subscribe to continue using Casey.'
          })
          setIsStreaming(false)
          setIsGenerating(false)
          return
        }

        // Handle chat protocol events
        if (event.event === 'message') {
          // Message event with delta content
          if (event.data?.delta) {
            appendToLastMessage(event.data.content || '')
            tokenCount++
          } else {
            // Full message replacement
            updateMessage(assistantMessage.id, { 
              content: event.data?.content || '' 
            })
          }
        } else if (event.event === 'artifact') {
          // Fire notification for document creation
          notificationCallbackRef.current?.('content_change', `${event.data?.type || 'Document'} created`)
          // Artifact created - persist to backend and auto-open panel.
          // Declared in outer scope so the panel only opens after a successful backend create:
          // this stays null on the error path, so the post-try expandArtifact is skipped.
          let createdArtifactMessage: ChatMessage | null = null
          try {
            const artifactContent = typeof event.data?.content === 'string'
              ? event.data.content
              : JSON.stringify(event.data?.content, null, 2)

            // Use the conversation ID for artifact storage
            // Artifacts are linked to conversations in the database
            if (!effectiveConversationId) {
              console.error('❌ Cannot create artifact: no conversation context available')
              throw new Error('Cannot create artifact without conversation context')
            }

            // Create artifact in backend with conversation ID as case_id
            console.log(`📝 Creating artifact for conversation: ${effectiveConversationId}`)
            debugLog('ChatContext.tsx:sendMessage:createArtifact', 'Creating artifact via API', {
              conversation_id: effectiveConversationId,
              type: event.data?.type,
              title: event.data?.title
            })
            const response = await api.post(`/api/v1/artifacts`, {
              case_id: effectiveConversationId,
              type: event.data?.type || 'custom',
              title: event.data?.title || 'Clinical Document',
              content: artifactContent
            })
            debugLog('ChatContext.tsx:sendMessage:createArtifact:response', 'Artifact API response', {
              artifact_id: response.data.artifact_id,
              version_id: response.data.version_id,
              success: !!response.data.artifact_id
            })
            console.log(`✅ Artifact created with ID: ${response.data.artifact_id}`)

            const { artifact_id } = response.data

            // Update existing assistant message with artifact (keep in same turn)
            createdArtifactMessage = {
              ...assistantMessage,
              artifact: {
                type: event.data?.type || 'custom',
                title: event.data?.title || 'Clinical Document',
                content: artifactContent,
                artifact_id,
              },
              metadata: event.data?.metadata,
            }
            updateMessage(assistantMessage.id, {
              artifact: createdArtifactMessage.artifact,
              metadata: event.data?.metadata,
            })
          } catch (error: unknown) {
            console.error('❌ Failed to create artifact:', error)
            debugLog('ChatContext.tsx:sendMessage:createArtifact:ERROR', 'Artifact creation FAILED', {
              error: String(error),
              errorMessage: error instanceof Error ? error.message : 'unknown'
            })
            // Still update message with artifact even if backend fails (graceful degradation)
            updateMessage(assistantMessage.id, {
              artifact: {
                type: event.data?.type || 'custom',
                title: event.data?.title || 'Clinical Document',
                content: typeof event.data?.content === 'string'
                  ? event.data.content
                  : JSON.stringify(event.data?.content, null, 2)
              },
              metadata: {
                ...event.data?.metadata,
                error: 'Failed to persist artifact'
              }
            })
          }

          // Auto-open the TipTap artifact panel only after a successful create
          // (outside the try/catch — panel-open is not an artifact-creation concern)
          if (createdArtifactMessage) {
            expandArtifact(createdArtifactMessage)
            console.log(`✅ Artifact opened: ${createdArtifactMessage.artifact?.artifact_id}`)
          }
        } else if (event.event === 'artifact_update') {
          // Fire notification for document update
          notificationCallbackRef.current?.('content_change', 'Document updated')
          // Artifact updated - refresh content in backend and UI
          try {
            const { artifact_id, content: newContent } = event.data || {}
            if (!artifact_id || !newContent) {
              console.warn('⚠️ artifact_update event missing artifact_id or content')
              continue
            }

            // Update artifact in backend (create new version)
            await api.post(`/api/v1/artifacts/${artifact_id}/versions`, {
              content: newContent,
              is_manual_save: false,
              edit_description: "AI update"
            })

            // Update local state - any message with this artifact
            setMessages(prev => prev.map(msg => {
              if (msg.artifact?.artifact_id === artifact_id && msg.artifact) {
                return {
                  ...msg,
                  artifact: {
                    type: msg.artifact.type,
                    title: msg.artifact.title,
                    content: newContent,
                    artifact_id: msg.artifact.artifact_id
                  }
                }
              }
              return msg
            }))

            // Update expanded artifact if it matches
            // Fix #233: Use ref to avoid stale closure during streaming
            if (expandedArtifactRef.current?.artifact?.artifact_id === artifact_id) {
              setExpandedArtifact(prev => prev ? {
                ...prev,
                artifact: {
                  ...prev.artifact!,
                  content: newContent
                }
              } : null)
            }

            // Auto-expand the updated artifact to show changes in canvas
            // Fix #234: Use ref to avoid stale closure during streaming
            const messageWithArtifact = messagesRef.current.find(m => m.artifact?.artifact_id === artifact_id)
            if (messageWithArtifact) {
              // Create updated message object with new content for expandArtifact
              const updatedMessage = {
                ...messageWithArtifact,
                artifact: {
                  ...messageWithArtifact.artifact!,
                  content: newContent
                }
              }
              expandArtifact(updatedMessage)
            }

            console.log(`✅ Artifact updated: ${artifact_id}`)

            // Refresh hydrationData so useCanvas tabs update via loadFromArtifacts
            refreshArtifactRef.current?.(artifact_id).catch(err =>
              console.error('[ChatContext] Artifact hydration refresh failed:', err)
            )
          } catch (error) {
            console.error('❌ Failed to update artifact:', error)
          }
        } else if (event.event === 'complete' || event.event === 'done') {
          // Stream complete — notify user
          notificationCallbackRef.current?.('agent_activity', 'Casey finished responding to your message')
          const latency = Date.now() - startTime
          updateMessage(assistantMessage.id, {
            metadata: {
              model: event.data?.model,
              tokens: tokenCount || event.data?.tokens,
              latency
            }
          })
          // Refresh patient panel — Casey may have updated clinical data via save tools
          refreshClinicalDataRef.current?.().catch(err =>
            console.error('[ChatContext] Post-completion clinical data refresh failed:', err)
          )
        } else if (event.event === 'error') {
          throw new Error(event.data?.message || event.message || 'Unknown error')
        }
      }
    } catch (error: unknown) {
      // Handle abort separately from errors
      const err = error as Error
      if (err.name === 'AbortError') {
        updateMessage(assistantMessage.id, {
          content: (prev: string) => prev + '\n\n_[Generation stopped]_'
        })
      } else {
        console.error('Error sending message:', error)
        updateMessage(assistantMessage.id, {
          content: '⚠️ Error: Failed to generate response. Please try again.\n\n' +
                   (import.meta.env.DEV ? `Debug: ${err.message || 'Unknown error'}` : '')
        })
      }
    } finally {
      setIsGenerating(false)
      setIsStreaming(false)
      setResearchStatus({ active: false, message: '' })
      setIsReconciling(false)

      // Persist toolCalls and fileCitations to message before resetting
      // Use refs to avoid stale closure issues
      const finalToolCalls = toolCallsRef.current
      const finalFileCitations = pendingFileCitationsRef.current
      if (finalToolCalls.length > 0 || finalFileCitations.length > 0) {
        setMessages(prev => prev.map(msg => {
          if (msg.id === assistantMessage.id) {
            return {
              ...msg,
              ...(finalFileCitations.length > 0 && { fileCitations: finalFileCitations }),
              metadata: {
                ...msg.metadata,
                ...(finalToolCalls.length > 0 && { toolCalls: finalToolCalls })
              }
            }
          }
          return msg
        }))
      }

      // Reset progress state and refs
      toolCallsRef.current = []
      pendingFileCitationsRef.current = []
      setStreamingProgress({
        currentPhase: 'idle',
        isThinking: false,
        toolCalls: [],
        thinkingContent: ''
      })
      abortControllerRef.current = null
    }
  // Fix #232-234: messages & expandedArtifact accessed via refs to prevent stale closures
  }, [context, currentConversationId, currentPatient, activeProjectId, editingMessageId, addMessage, updateMessage, appendToLastMessage, triggerSidebarRefresh, files, patientData, loadPatientData, clearFiles])

  // ── Multi-Agent Case Panel Functions ──────────────────────────────────────

  /**
   * Initialize an agent session with an optional greeting message.
   * No-op if the session already exists.
   */
  const initAgentSession = useCallback((agentId: string, greeting?: string) => {
    setAgentSessions(prev => {
      if (prev[agentId]) return prev
      const initialMessages: AgentChatMessage[] = greeting
        ? [{
            id: `greeting-${agentId}`,
            role: 'assistant',
            content: greeting,
            agentId,
            tools: [],
            timestamp: Date.now()
          }]
        : []
      return {
        ...prev,
        [agentId]: {
          agentId,
          messages: initialMessages,
          isStreaming: false,
          isLoading: false,
          conversationId: null,
          error: null
        }
      }
    })
  }, [])

  /**
   * Reset an agent session, clearing all messages and state.
   */
  const resetAgentSession = useCallback((agentId: string) => {
    // Abort any in-flight request
    if (agentAbortControllers.current[agentId]) {
      agentAbortControllers.current[agentId].abort()
      delete agentAbortControllers.current[agentId]
    }
    setAgentSessions(prev => {
      const next = { ...prev }
      delete next[agentId]
      return next
    })
  }, [])

  const clearAgentSessions = useCallback(() => {
    Object.values(agentAbortControllers.current).forEach(c => c.abort())
    agentAbortControllers.current = {}
    setAgentSessions({})
  }, [])

  /**
   * Load agent sessions from hydration data (backend conversations).
   * Populates agentSessions so CasePanelChat shows all agent tabs.
   */
  const loadAgentSessions = useCallback((
    conversations: Array<{ id: string; agent_id: string; messages: Array<{ id: string; role: string; content: string; created_at: string; metadata?: Record<string, any>; thinking_content?: string | null; thinking_duration_ms?: number | null }> }>
  ) => {
    setAgentSessions(prev => {
      // Identify which sessions are actively streaming — don't touch those
      const streamingIds = new Set(
        Object.entries(prev)
          .filter(([, s]) => s.isStreaming || s.isLoading)
          .map(([id]) => id)
      )

      // Only abort controllers for sessions we're about to replace
      Object.entries(agentAbortControllers.current).forEach(([id, c]) => {
        if (!streamingIds.has(id)) {
          c.abort()
          delete agentAbortControllers.current[id]
        }
      })

      const next: Record<string, AgentSession> = {}

      for (const conv of conversations) {
        if (!conv.agent_id) continue

        // Preserve actively streaming sessions — hydration data would be stale
        if (streamingIds.has(conv.agent_id)) {
          next[conv.agent_id] = prev[conv.agent_id]
          continue
        }

        const filtered = conv.messages.filter(m => m.role === 'user' || m.role === 'assistant')
        // Mark pipeline-automated messages so the renderer can hide them
        const pipelineUserIds = new Set<number>()
        filtered.forEach((m, i) => {
          if (m.role === 'user' && isPipelineUserMessage(m.content)) pipelineUserIds.add(i)
        })

        // A persisted consult turn is a RUN of contiguous assistant rows bounded by
        // user rows (after the MD-echo fix there is NO round-trip user row between
        // legs). A 1-hop turn persists [ Casey leg0, consult, Casey leg2 ]; a 2-hop
        // turn (cap = 2) persists [ leg0, consult1, leg1, consult2, leg2 ]. Fold each
        // such run into ONE Casey message: the consult rows (metadata.agentId) become
        // the embedded `consult[]` pills (dispatch order) and the LAST plain Casey row
        // is the synthesis. Splitting on user rows makes turn boundaries unambiguous —
        // no fold can bleed across turns.
        const isConsultRow = (m: typeof filtered[number]) => m.role === 'assistant' && !!m.metadata?.agentId
        const baseOf = (m: typeof filtered[number], i: number) => ({
          tools: [] as AgentToolCall[],
          timestamp: new Date(m.created_at).getTime(),
          fromPipeline: pipelineUserIds.has(i) || (m.role === 'assistant' && i > 0 && pipelineUserIds.has(i - 1)),
        })

        const agentMessages: AgentChatMessage[] = []
        let i = 0
        while (i < filtered.length) {
          const m = filtered[i]
          if (m.role === 'user') {
            agentMessages.push({
              ...baseOf(m, i),
              id: m.id,
              role: 'user',
              content: m.content,
              agentId: conv.agent_id,
            })
            i++
            continue
          }
          // Gather the run of contiguous assistant rows for this turn.
          let j = i
          const consultRows: typeof filtered = []
          const plainRows: typeof filtered = []  // Casey legs, in persisted order
          while (j < filtered.length && filtered[j].role === 'assistant') {
            if (isConsultRow(filtered[j])) consultRows.push(filtered[j])
            else plainRows.push(filtered[j])
            j++
          }
          const synthesisRow = plainRows.length > 0 ? plainRows[plainRows.length - 1] : null
          if (consultRows.length > 0) {
            // Fold the whole run into ONE Casey message. Content = the last plain
            // Casey row (the synthesis); reasoning = EVERY plain leg's phases joined
            // in order with the phase separator, durations summed — live streaming
            // accumulates leg0+leg2 reasoning into one panel (beginThinkingPhase
            // appends across legs), so reload must show the same trace, not just
            // the final leg's. If the run was ALL consult rows (a disconnect before
            // Casey synthesized), render NO synthesis: empty content (the bubble is
            // gated on content) and no reasoning — the consult pills stand alone.
            // Never echo the advisory as Casey's content (it already renders in the
            // pill), and never surface the consult's own reasoning on Casey's bubble.
            const thinkingParts = plainRows
              .map(r => r.thinking_content)
              .filter((t): t is string => !!t)
            const thinkingDurations = plainRows
              .map(r => r.thinking_duration_ms)
              .filter((d): d is number => typeof d === 'number')
            agentMessages.push({
              ...baseOf(filtered[i], i),
              id: filtered[i].id,                  // first row of the turn (stable anchor)
              role: 'assistant',
              content: synthesisRow?.content ?? '',
              agentId: conv.agent_id,              // Casey — NOT a consult persona
              thinkingContent: thinkingParts.length > 0
                ? thinkingParts.join(THINKING_PHASE_SEPARATOR)
                : undefined,
              thinkingDurationMs: thinkingDurations.length > 0
                ? thinkingDurations.reduce((a, b) => a + b, 0)
                : undefined,
              consult: consultRows.map(c => ({
                agent: c.metadata!.agentId as string,
                reason: c.metadata!.consultReason as string | undefined,
                content: c.content,
                status: 'done' as const,
              })),
            })
          } else {
            // No consults — emit each assistant row standalone (normally just one).
            for (let k = i; k < j; k++) {
              const row = filtered[k]
              agentMessages.push({
                ...baseOf(row, k),
                id: row.id,
                role: 'assistant',
                content: row.content,
                agentId: row.metadata?.agentId ?? conv.agent_id,
                thinkingContent: row.thinking_content ?? undefined,
                thinkingDurationMs: row.thinking_duration_ms ?? undefined,
              })
            }
          }
          i = j
        }
        next[conv.agent_id] = {
          agentId: conv.agent_id,
          messages: agentMessages,
          isStreaming: false,
          isLoading: false,
          conversationId: conv.id,
          error: null,
        }
      }

      // Carry forward any streaming sessions not in the hydration data
      for (const [id, session] of Object.entries(prev)) {
        if (streamingIds.has(id) && !next[id]) {
          next[id] = session
        }
      }

      return next
    })
  }, [])

  /**
   * Switch the active agent in the chat panel.
   */
  const switchAgent = useCallback((agentId: string) => {
    setActiveAgentId(agentId)
  }, [])

  /**
   * Get messages for a specific agent session.
   */
  const getSessionMessages = useCallback((agentId: string): AgentChatMessage[] => {
    return agentSessions[agentId]?.messages || []
  }, [agentSessions])

  /**
   * Whether a turn is currently in flight for `agentId`. Keyed on the live abort
   * controller — the exact condition the single-in-flight guard in sendToAgent uses
   * to refuse an overlapping turn. Callers (e.g. the case-panel composer) use this to
   * avoid optimistically clearing input for a send that would be refused. A ref read,
   * so it reflects the in-flight state synchronously within the same click handler.
   */
  const isAgentResponding = useCallback((agentId: string): boolean => {
    return !!agentAbortControllers.current[agentId]
  }, [])

  /**
   * Send a message to a specific agent via the case panel SSE endpoint.
   * Creates the session if it doesn't exist, handles streaming, tool calls,
   * and canvas updates.
   */
  const sendToAgent = useCallback(async (
    agentId: string,
    message: string,
    mode: string = 'edit',
    canvasSnapshots?: Record<string, string>,
    options?: { hidden?: boolean; fileIds?: string[]; orchestrate?: boolean }
  ): Promise<{ output: string; tools: AgentToolCall[] } | null> => {
    if (!message.trim()) return null

    // Single-in-flight guard. The case panel drives one agent from several places
    // at once — the physician's composer, the observer's auto-reviews (useObserver),
    // and orchestration relays (useOrchestration) — none of which coordinate. If a
    // second turn starts while one is already streaming, the session ends up with two
    // assistant messages both `isStreaming: true`, and the chat renders two avatars +
    // two progress panels (issue #196). The regular chat never hits this because a
    // single conversation has exactly one in-flight turn structurally; this guard gives
    // the per-agent sessions the same invariant. The abort controller is registered
    // synchronously below (before the first await), so even same-tick calls serialize.
    // The backend rejects overlapping same-agent requests anyway (per-conversation lock in
    // agent_runner.py), and every caller treats a null return as "no action", so skipping the
    // redundant turn is safe today. NOTE: the useOrchestration/useObserver relays currently
    // receive a no-op sendToAgent stub (WorkspaceProvider is mounted unwired in App.tsx), so
    // they never reach this guard. If that wiring is completed, a dropped relay (e.g.
    // refreshPatientPanel's med-rec refresh) should be surfaced/queued, not silently skipped —
    // tracked in issue #41.
    if (agentAbortControllers.current[agentId]) {
      console.warn(`[ChatContext] sendToAgent('${agentId}') skipped — a turn is already in flight`)
      return null
    }

    const isHidden = options?.hidden === true
    // Orchestration (relay/consult) is on by default for physician-typed turns.
    // Background/pipeline callers (useObserver, and every system-composed turn in
    // useOrchestration) pass orchestrate:false, so a stray tagged consult event is
    // dropped outright and never grows a pill in their single-leg session.
    const orchestrate = options?.orchestrate !== false

    // Ensure session exists
    setAgentSessions(prev => {
      if (!prev[agentId]) {
        return {
          ...prev,
          [agentId]: {
            agentId,
            messages: [],
            isStreaming: false,
            isLoading: false,
            conversationId: null,
            error: null
          }
        }
      }
      return prev
    })

    // Add user message (skip for hidden/pipeline messages)
    const userMsg: AgentChatMessage = {
      id: `user-${crypto.randomUUID()}`,
      role: 'user',
      content: message,
      agentId,
      tools: [],
      timestamp: Date.now(),
      fromPipeline: isHidden,
    }

    // Create assistant placeholder
    const assistantMsgId = `assistant-${crypto.randomUUID()}`
    const assistantMsg: AgentChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      agentId,
      tools: [],
      isStreaming: true,
      timestamp: Date.now(),
      fromPipeline: isHidden,
    }

    setAgentSessions(prev => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        messages: [
          ...(prev[agentId]?.messages || []),
          // Skip user message for hidden (pipeline) calls
          ...(isHidden ? [] : [userMsg]),
          assistantMsg,
        ],
        isStreaming: true,
        isLoading: !isHidden,
        error: null
      }
    }))

    // Create abort controller for this agent with 8-minute safety timeout
    const abortController = new AbortController()
    agentAbortControllers.current[agentId] = abortController
    const requestTimeout = setTimeout(() => abortController.abort(), 8 * 60 * 1000)

    // Request-leg streaming buffer. The request leg (the agent the caller
    // addressed — Casey for orchestrated turns) owns the only assistant message
    // and is the ONLY leg whose content is returned to the caller. A consult
    // (relay) leg's advisory text streams into `message.consult.content` on this
    // same turn (see relay_start + the foreign-tagged message handler), so there
    // is no separate consult leg buffer.
    const requestLegAgent = agentId
    interface LegState {
      msgId: string
      content: string
      /** Casey prose discarded on relay_start, restorable if the consult drops
          with no synthesis leg (else the bubble renders empty while the dropped
          pill claims "Casey answered directly"). Mirrors reload, where the last
          persisted Casey row's prose is what shows. */
      preConsultContent: string
      /** Set ONLY by a genuine-failure relay_dropped that found the buffer empty:
          the restored prose the finalize must keep if no later Casey leg streams
          (the buffer stays '' so a post-drop leg replaces, never appends). */
      dropRestoredContent: string
      thinking: string
      tools: AgentToolCall[]
      thinkingPhaseBase: string
      thinkingStartTime: number
    }
    const legs = new Map<string, LegState>()
    legs.set(requestLegAgent, {
      msgId: assistantMsgId,
      content: '',
      preConsultContent: '',
      dropRestoredContent: '',
      thinking: '',
      tools: [],
      thinkingPhaseBase: '',
      thinkingStartTime: 0,
    })

    // Embedded-consult model: the request (Casey) leg owns the ONLY assistant
    // message. A consult (relay) leg no longer spawns a sibling bubble — its
    // advisory text is appended to `message.consult.content` on the SAME Casey
    // turn (set up on relay_start). Foreign-tagged non-message events (the
    // consult leg's thinking_*/tool_*) are dropped for v1 (see the loop below).
    //
    // `isConsultEvent` decides whether a tagged event belongs to the consult leg
    // (orchestrated turns only). When orchestration is off, foreign-tagged events
    // are DROPPED outright (spec: the return value is the request-leg buffer only —
    // background callers' parsers must never see another leg's text) and no
    // consult pill ever grows.
    const requestLeg = (): LegState => legs.get(requestLegAgent)!
    const isForeignEvent = (rawAgent: string | undefined): boolean =>
      !!rawAgent && rawAgent !== requestLegAgent
    const isConsultEvent = (rawAgent: string | undefined): boolean =>
      orchestrate && isForeignEvent(rawAgent)

    try {
      // Use workspace ID from ref (set by WorkspaceContext) or conversation fallback
      const workspaceId = activeWorkspaceIdRef.current || conversationIdRef.current || crypto.randomUUID()

      const eventStream = backendAdapter.streamCasePanelChat({
        workspaceId,
        agentId,
        message,
        mode,
        canvasSnapshots,
        files: options?.fileIds,
        orchestrate,
        signal: abortController.signal,
      })

      for await (const event of eventStream) {
        const eventType = event.event
        const data = event.data

        // Route every event to its leg (request leg unless tagged with another
        // agent_id and orchestration is on). The reducers below all target the
        // SAME Casey session (`requestLegAgent`) but update the leg's own msgId.
        const eventLegAgent = (data.agent_id as string | undefined)

        // Non-orchestrated turns drop foreign-tagged events wholesale (spec:
        // "ignore events whose agent_id differs from the request leg"). Warn —
        // reaching here means the backend tagged a leg the caller can't render.
        if (!orchestrate && isForeignEvent(eventLegAgent)) {
          console.warn(
            `[sendToAgent] dropped foreign-tagged "${eventType}" event (agent_id=${eventLegAgent}) on a non-orchestrated turn`
          )
          continue
        }

        if (eventType === 'relay_start') {
          // Embed a consult pill on the request-leg (Casey) message and DISCARD
          // the leg0 intro prose: the chip + pill replace it, and leg2's synthesis
          // starts clean. No sibling bubble. The consult agent comes from
          // `data.agent` (the relay target), not `data.agent_id`. A 2nd relay in
          // the same turn (multi-hop, cap = 2) APPENDS a new pill — and settles the
          // prior consult to 'done', since the backend round-tripped it to get here.
          if (orchestrate) {
            const consultAgent = (data.agent as string | undefined) || requestLegAgent
            const consultReason = data.reason as string | undefined
            const leg = requestLeg()
            leg.preConsultContent = leg.content
            leg.content = ''
            const legMsgId = leg.msgId
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  messages: session.messages.map(m =>
                    m.id === legMsgId
                      ? {
                          ...m,
                          content: '',
                          consult: [
                            ...settleStreamingConsults(m.consult ?? [], 'done'),
                            { agent: consultAgent, reason: consultReason, content: '', status: 'streaming' as const },
                          ],
                        }
                      : m
                  ),
                },
              }
            })
          }
        } else if (eventType === 'relay_dropped') {
          // A consult was dropped. Fail safe: ONLY cap_exceeded/invalid_target reject
          // a NEW relay while the active consult already SUCCEEDED (→ 'done'). Every
          // other outcome — timeout/error/empty AND any unrecognized future outcome —
          // is a failure (→ 'dropped'), so an unknown drop never renders as a green ✓.
          // Already-terminal consults are never downgraded.
          const leg = requestLeg()
          const legMsgId = leg.msgId
          const outcome = data.outcome as string | undefined
          const consultSucceeded = outcome === 'cap_exceeded' || outcome === 'invalid_target'
          // A genuine failure (timeout/error/empty) means no synthesis leg follows:
          // restore the leg0 prose discarded on relay_start so the bubble isn't
          // empty — the dropped pill's "Casey answered directly" must be true.
          // Matches reload, which re-shows the persisted leg0 row. Display-level
          // only: the leg buffer stays '' so a Casey leg streaming AFTER the drop
          // (cap/invalid rejections) replaces the restored prose, never appends.
          if (!consultSucceeded && leg.content === '') {
            leg.dropRestoredContent = leg.preConsultContent
          }
          const restoredContent = leg.content || leg.dropRestoredContent
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId && m.consult
                    ? {
                        ...m,
                        content: restoredContent,
                        consult: settleStreamingConsults(m.consult, consultSucceeded ? 'done' : 'dropped'),
                      }
                    : m
                ),
              },
            }
          })
        } else if (eventType === 'message' || eventType === 'token') {
          if (isConsultEvent(eventLegAgent)) {
            // Foreign-tagged token = the consult leg's advisory. Append to the
            // active (last streaming) consult on the Casey turn — never to Casey's
            // content and never to a sibling bubble.
            const legMsgId = requestLeg().msgId
            const delta = (data.content as string) || ''
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  messages: session.messages.map(m =>
                    m.id === legMsgId && m.consult
                      ? { ...m, consult: appendToActiveConsult(m.consult, delta) }
                      : m
                  ),
                },
              }
            })
          } else {
            // Casey-tagged or untagged token → the request-leg content (this is
            // leg2 synthesis after a relay, or normal content otherwise). A Casey
            // token AFTER a consult means the backend round-tripped it (the
            // consult leg is fully drained before leg2 starts), so settle the
            // pill to 'done' NOW — G6.5 collapses on consult completion, not at
            // the terminal complete seconds later.
            const leg = requestLeg()
            leg.content += data.content || ''
            const legContent = leg.content
            const legMsgId = leg.msgId
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  messages: session.messages.map(m =>
                    m.id === legMsgId
                      ? {
                          ...m,
                          content: legContent,
                          ...(m.consult ? { consult: settleStreamingConsults(m.consult, 'done') } : {}),
                        }
                      : m
                  )
                }
              }
            })
          }
        } else if (eventType === 'thinking_start') {
          // v1: the consult leg's reasoning is NOT rendered — only its `message`
          // content feeds the pill. Drop foreign-tagged thinking events.
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          leg.thinkingPhaseBase = beginThinkingPhase(leg.thinking)
          leg.thinking = leg.thinkingPhaseBase
          leg.thinkingStartTime = Date.now()
          const legThinking = leg.thinking
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId ? { ...m, thinkingContent: legThinking, isThinking: true } : m
                )
              }
            }
          })
        } else if (eventType === 'thinking_delta') {
          // v1: drop the consult leg's reasoning (see thinking_start).
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          leg.thinking += data.content || ''
          const legThinking = leg.thinking
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId ? { ...m, thinkingContent: legThinking, isThinking: true } : m
                )
              }
            }
          })
        } else if (eventType === 'thinking_complete') {
          // v1: drop the consult leg's reasoning (see thinking_start).
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          leg.thinking = finalizeThinkingPhase(leg.thinking, leg.thinkingPhaseBase, (data.content as string) || '')
          // Prefer the provider-reported duration so live matches the persisted value
          // (backend persists the SUM of provider duration_ms); fall back to wall-clock.
          const providerMs = Number(data.duration_ms) || 0
          const phaseMs = providerMs > 0 ? providerMs : (leg.thinkingStartTime ? Date.now() - leg.thinkingStartTime : 0)
          const legThinking = leg.thinking
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId
                    ? { ...m, thinkingContent: legThinking, thinkingDurationMs: (m.thinkingDurationMs || 0) + phaseMs, isThinking: false }
                    : m
                )
              }
            }
          })
        } else if (eventType === 'skill_loaded') {
          // Auto-loaded skill: push a synthetic completed tool row so the
          // ToolCard shows "Loaded Skill: <name>" alongside real tool calls.
          // v1: drop the consult leg's tool/skill activity (not rendered).
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          const skillName = String(data.skill_name || 'unknown')
          leg.tools.push({
            name: 'load_skill',
            callId: `skill-${skillName}`,
            input: skillName,
            output: '',
            isRunning: false,
            success: true,
            skillLabel: `Loaded Skill: ${skillName}`
          })
          const legTools = [...leg.tools]
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId ? { ...m, tools: legTools } : m
                )
              }
            }
          })
        } else if (eventType === 'tool_start') {
          // v1: drop the consult leg's tool activity (not rendered).
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          leg.tools.push({
            name: String(data.tool_name || data.tool || 'unknown'),
            callId: String(data.call_id || ''),
            input: typeof data.input === 'string' ? data.input : JSON.stringify(data.input || ''),
            output: '',
            isRunning: true,
            args: (data.args as Record<string, unknown> | undefined) || undefined
          })
          const legTools = [...leg.tools]
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId ? { ...m, tools: legTools } : m
                )
              }
            }
          })
        } else if (eventType === 'tool_end') {
          // Match by call_id first (correct under concurrent same-named tools);
          // fall back to name+isRunning only for legacy streams without call_id.
          // v1: drop the consult leg's tool activity (not rendered).
          if (isConsultEvent(eventLegAgent)) continue
          const leg = requestLeg()
          const eventCallId = String(data.call_id || '')
          const eventToolName = String(data.tool_name || data.tool || '')
          const matchIndex = matchToolEndIndex(leg.tools, eventCallId, eventToolName)
          const running = matchIndex === -1 ? undefined : leg.tools[matchIndex]
          if (running) {
            running.output = typeof data.output === 'string' ? data.output : JSON.stringify(data.output || '')
            running.isRunning = false
            running.success = (data.success as boolean | undefined) ?? true
            running.citationCount = typeof data.citation_count === 'number' ? data.citation_count : undefined
            running.extractionDegraded = data.extraction_degraded === true
          }
          const legTools = [...leg.tools]
          const legMsgId = leg.msgId
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId ? { ...m, tools: legTools } : m
                )
              }
            }
          })
          // Fire notification for tool completions in case panel
          const agentToolName = (data.tool_name || data.tool) as string | undefined
          if (agentToolName) {
            const isClinical = ['drug_interaction_check', 'gap_analysis', 'search_clinical_evidence'].includes(agentToolName)
            notificationCallbackRef.current?.(
              isClinical ? 'clinical_alert' : 'agent_activity',
              `${formatToolDisplayName(agentToolName)} completed`
            )
          }
        } else if (eventType === 'artifact') {
          // v1: drop the consult leg's canvas updates (not rendered, no notify).
          if (isConsultEvent(eventLegAgent)) continue
          // Fire notification for canvas update from agent
          const artifactDisplayNames: Record<string, string> = {
            'hpi': 'HPI note updated',
            'pe': 'Physical Exam note updated',
            'hpi_pe': 'HPI & Physical Exam updated',
            'assessment_and_plan': 'Assessment & Plan updated',
            'ap': 'Assessment & Plan updated',
            'notes': 'Clinical notes updated',
          }
          const artifactMsg = artifactDisplayNames[(data.artifact_type as string)] || `${(data.artifact_type as string) || 'Document'} updated`
          notificationCallbackRef.current?.('content_change', artifactMsg)
          // Canvas update from agent
          const leg = requestLeg()
          const legMsgId = leg.msgId
          const canvasUpdate: CanvasUpdate = {
            tab: (data.artifact_type as string) || 'custom',
            content: (data.content as string) || '',
            version: data.version as number | undefined
          }
          setAgentSessions(prev => {
            const session = prev[requestLegAgent]
            if (!session) return prev
            return {
              ...prev,
              [requestLegAgent]: {
                ...session,
                messages: session.messages.map(m =>
                  m.id === legMsgId
                    ? { ...m, canvasUpdates: [...(m.canvasUpdates || []), canvasUpdate] }
                    : m
                )
              }
            }
          })
        } else if (eventType === 'artifact_update') {
          // Canvas content update from tool (save_hpi_pe, save_ap, generate_hpi_pe, generate_ap)
          const tab = (data.artifact_type as string) || ''
          const content = (data.content as string) || ''
          if (tab && content) {
            canvasUpdateRef.current?.(tab, content)

            const artifactDisplayNames: Record<string, string> = {
              'hpi_pe': 'HPI & Physical Exam updated',
              'ap': 'Assessment & Plan updated',
            }
            const displayMsg = artifactDisplayNames[tab] || `${tab} updated`
            notificationCallbackRef.current?.('content_change', displayMsg)
          }
        } else if (eventType === 'complete' || eventType === 'done') {
          // Refresh patient panel — agent may have updated clinical data via save tools
          refreshClinicalDataRef.current?.().catch(err =>
            console.error('[ChatContext] Post-agent clinical data refresh failed:', err)
          )
          // Notify user that agent finished
          const agentDisplayNames: Record<string, string> = {
            'casey': 'Casey finished processing chart data',
            'hpi-pe-agent': 'The Historian finished writing HPI & PE',
            'clinical-reasoning': 'The Resident finished Assessment & Plan',
          }
          const finishMsg = agentDisplayNames[agentId]
            || `${agentId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} finished`
          notificationCallbackRef.current?.('agent_activity', finishMsg)
          // Use final output if provided. `complete` finalizes the REQUEST
          // (Casey) leg — `data.output` is Casey's full reply, never a consult.
          const completeLeg = requestLeg()
          const finalContent = (data.output as string) || completeLeg.content
          if (finalContent && finalContent !== completeLeg.content) {
            completeLeg.content = finalContent
          }
          // Store conversation ID if returned
          if (data.conversation_id) {
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  conversationId: data.conversation_id as string
                }
              }
            })
          }
          // Capture last-turn latency/token metadata for the canvas footer.
          // Read with optional chaining so the frontend tolerates a backend
          // that has not yet shipped the extended `complete` payload.
          const lm = data.latency_ms as number | undefined
          if (lm) {
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  lastTurnMeta: {
                    latencyMs: lm,
                    totalTokens: data.total_tokens as number | undefined,
                  },
                },
              }
            })
          }
        } else if (eventType === 'error') {
          // Friendly handling for concurrent request (agent busy)
          if (data.code === 'concurrent_request') {
            const busyAgentId = (data.agent_id as string) || agentId
            const agentNames: Record<string, string> = {
              casey: 'Casey is', 'hpi-pe-agent': 'The Historian is', 'clinical-reasoning': 'The Resident is',
            }
            const busyMsg = `${agentNames[busyAgentId] || 'This agent is'} still working on the documentation. Your message will be handled once it finishes.`
            const requestLegMsgId = legs.get(requestLegAgent)!.msgId
            // SITE 2 — early-return path. This bypasses the in-loop finalize
            // below, so it must ALSO settle any open embedded consult + clear the
            // pill here, or the consult pill spins forever.
            setAgentSessions(prev => {
              const session = prev[requestLegAgent]
              if (!session) return prev
              return {
                ...prev,
                [requestLegAgent]: {
                  ...session,
                  messages: session.messages.map(m =>
                    m.id === requestLegMsgId
                      ? {
                          ...m,
                          content: busyMsg,
                          isStreaming: false,
                          isThinking: false,
                          ...(m.consult ? { consult: settleConsultsOnInterrupt(m.consult) } : {}),
                        }
                      : m
                  ),
                  isStreaming: false,
                  isLoading: false,
                }
              }
            })
            return { output: '', tools: [] }
          } else {
            throw new Error((data.error as string) || (data.message as string) || 'Unknown streaming error')
          }
        }
      }

      // SITE 1 — clean-complete finalize. Finalize the request (Casey) leg with
      // its cleaned tools + final content, clear the pill, and promote any still
      // -streaming embedded consult to 'done'. Return ONLY the request leg's
      // buffer (consult text is never returned to the caller).
      const finalRequestLeg = legs.get(requestLegAgent)!
      const requestCleanedTools = finalRequestLeg.tools.map(({ isRunning, ...t }) => t)
      const finalRequestLegMsgId = finalRequestLeg.msgId
      setAgentSessions(prev => {
        const session = prev[requestLegAgent]
        if (!session) return prev
        return {
          ...prev,
          [requestLegAgent]: {
            ...session,
            isStreaming: false,
            isLoading: false,
            messages: session.messages.map(m => {
              if (m.id === finalRequestLegMsgId) {
                return {
                  ...m,
                  // A drop-ended turn (empty buffer) keeps the prose restored by
                  // relay_dropped; any post-drop Casey leg (non-empty buffer) wins.
                  content: finalRequestLeg.content || finalRequestLeg.dropRestoredContent,
                  tools: finalRequestLeg.tools.map(({ isRunning, ...t }) => t),
                  isStreaming: false,
                  isThinking: false,
                  // Any open consult settles to 'done' on a clean finish.
                  ...(m.consult ? { consult: settleStreamingConsults(m.consult, 'done') } : {}),
                }
              }
              return m
            })
          }
        }
      })

      // Return response directly so callers don't need to read stale state
      return { output: finalRequestLeg.content, tools: requestCleanedTools }
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Unknown error'
      const isAbort = err instanceof Error && err.name === 'AbortError'

      // Classify error for user-friendly display
      const isConnectionError = /SSE stream error|network|fetch|Failed to fetch|ERR_CONNECTION/i.test(rawMessage)
      const errorMessage = isConnectionError
        ? 'Connection lost -- the response was interrupted. Please try sending your message again.'
        : rawMessage

      // SITE 3 — error/abort path. Finalize the request (Casey) leg, clear the
      // pill, and settle any open embedded consult so a mid-consult throw can't
      // leave a forever-spinning pill. Like the relay_dropped path, fall back to
      // the pre-relay snapshot: a throw between relay_start (which cleared the
      // buffer) and the synthesis leg must not vanish Casey's visible prose.
      const requestLegMsgId = legs.get(requestLegAgent)!.msgId
      const errorLeg = legs.get(requestLegAgent)!
      const requestLegContent =
        errorLeg.content || errorLeg.dropRestoredContent || errorLeg.preConsultContent
      setAgentSessions(prev => {
        const session = prev[agentId]
        if (!session) return prev
        return {
          ...prev,
          [agentId]: {
            ...session,
            isStreaming: false,
            isLoading: false,
            error: isAbort ? null : errorMessage,
            messages: session.messages.map(m => {
              if (m.id === requestLegMsgId) {
                return {
                  ...m,
                  content: isAbort
                    ? (m.content || requestLegContent) + '\n\n_[Generation stopped]_'
                    : m.content || requestLegContent || `Error: ${errorMessage}`,
                  isStreaming: false,
                  isThinking: false,
                  ...(m.consult ? { consult: settleConsultsOnInterrupt(m.consult) } : {}),
                }
              }
              return m
            })
          }
        }
      })
      return null
    } finally {
      clearTimeout(requestTimeout)
      delete agentAbortControllers.current[agentId]
    }
  }, [])

  /**
   * Trigger reconciliation of patient data with conversation and documents.
   * Sends a message to Casey asking to analyze all sources and propose updates.
   */
  const reconcilePatientData = useCallback(() => {
    setIsReconciling(true)
    // Send the reconciliation prompt as a user message
    // sendMessage will handle the streaming and isReconciling will be reset in its finally block
    sendMessage(RECONCILE_PATIENT_DATA_PROMPT)
  }, [sendMessage])

  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  /**
   * Retry from a specific user message.
   * Deletes the message and subsequent messages from the database,
   * removes them locally, then resends.
   */
  const retryFromMessage = useCallback(async (messageId: string) => {
    // Fix #235: Use ref to avoid stale closure
    const currentMessages = messagesRef.current
    const messageIndex = currentMessages.findIndex(m => m.id === messageId)
    if (messageIndex === -1) return

    const targetMessage = currentMessages[messageIndex]
    if (targetMessage.role !== 'user') return

    const contentToRetry = targetMessage.content
    const seq = targetMessage.seq

    // Delete from backend first (if message was persisted with a seq number)
    if (currentConversationId && seq) {
      try {
        const token = await getAuthToken()
        const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || ''
        const response = await fetch(`${API_BASE_URL}/api/v1/conversations/${currentConversationId}/messages/from/${seq}`, {
          method: 'DELETE',
          headers: {
            'Authorization': token || ''
          }
        })

        if (!response.ok) {
          throw new Error(`Backend deletion failed: ${response.status}`)
        }
      } catch (error) {
        console.error('Failed to delete messages for retry:', error)
        return  // Abort - don't proceed with local removal if backend failed
      }
    }

    // Remove this message and all subsequent messages locally
    setMessages(prev => prev.slice(0, messageIndex))
    // Resend the message
    sendMessage(contentToRetry)
  }, [currentConversationId, sendMessage])

  /**
   * Populate input with message content for editing.
   * Stores the message ID being edited for removal when user submits.
   */
  const editMessage = useCallback((messageId: string) => {
    // Fix #236: Use ref to avoid stale closure
    const message = messagesRef.current.find(m => m.id === messageId)
    if (!message || message.role !== 'user') return

    setEditingMessageId(messageId)
    // Strip attachment text from content before populating input
    const cleanContent = message.content.replace(/\n\n\[Attached File:.*?\]\n.*/gs, '')
    setUserInput(cleanContent)
  }, [setUserInput])

  const clearChat = useCallback(() => {
    setMessages([])
    setArtifacts([])
    setContextState({})
  }, [])

  // Load most recent session on mount
  // Security fix: AbortController prevents race conditions on unmount
  // Auth fix: Support both Microsoft and Google authentication
  useEffect(() => {
    const controller = new AbortController()
    let mounted = true
    let retryCount = 0
    const MAX_RETRIES = 5
    const RETRY_DELAY_MS = 500

    const loadRecentSession = async () => {
      // Set loading state at the start
      setIsLoadingSessions(true)

      // Check if authentication is ready for either Google or Microsoft
      const authToken = await getAuthToken()

      if (!authToken) {
        // Retry with delay if auth isn't ready yet (MSAL restoration in progress)
        if (retryCount < MAX_RETRIES && mounted) {
          retryCount++
          console.log(`Waiting for authentication (attempt ${retryCount}/${MAX_RETRIES})...`)
          setTimeout(loadRecentSession, RETRY_DELAY_MS)
        } else {
          // Stop showing loading state if we've exhausted retries
          if (mounted) {
            setIsLoadingSessions(false)
          }
        }
        return
      }

      try {
        // BUGFIX: Use /api/conversations instead of /api/v1/chat/sessions
        // The streaming /api/chat endpoint creates conversations, not chat_sessions
        const response = await api.get('/api/conversations', {
          signal: controller.signal
        })

        // Only update state if component is still mounted
        if (mounted && response.data) {
          // Clear any previous errors on successful load
          setSessionError(null)
          // response.data = { conversations: [...] }
          const conversations = response.data.conversations || []
          setSessions(conversations)
          setIsLoadingSessions(false)

          // Sessions loaded - WorkspaceManager handles default state
          // User must explicitly click a session to load it
        }
      } catch (error: unknown) {
        // Handle 401 specifically - auth not ready yet
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          console.log('Authentication not ready, skipping session load')
          if (mounted) {
            setIsLoadingSessions(false)
          }
          return
        }

        // Ignore abort errors (component unmounted)
        if (error instanceof Error && error.name !== 'AbortError' && mounted) {
          console.error('Failed to load sessions:', error)
          setSessionError('Failed to load chat history')
          setIsLoadingSessions(false)
        }
      }
    }

    loadRecentSession()

    return () => {
      mounted = false
      controller.abort()
    }
  }, [msalInstance, msalAccounts, loadSession])  // Re-run when MSAL accounts change (auth restored)

  // Reload sessions when shouldRefreshSidebar changes (triggered when new conversation created)
  useEffect(() => {
    const reloadSessions = async () => {
      // Check if authentication is ready for either Google or Microsoft
      const authToken = await getAuthToken()

      if (!authToken) {
        return
      }

      try {
        const response = await api.get('/api/conversations')
        if (response.data) {
          const conversations = response.data.conversations || []
          setSessions(conversations)
        }
      } catch (error) {
        console.error('Failed to reload sessions:', error)
      }
    }

    reloadSessions()
  }, [shouldRefreshSidebar, msalAccounts])  // Use msalAccounts to react to auth changes

  return (
    <ChatContext.Provider value={{
      // State
      messages,
      artifacts,
      files,
      context,
      sessions,
      currentSessionId,
      currentConversationId,
      sessionError,
      isLoadingSession,
      isLoadingSessions,
      shouldRefreshSidebar,
      isPanelOpen,
      isStreaming,
      isGenerating,
      expandedArtifact,
      currentPatient,
      isCommandPickerOpen,
      commandPrefix,
      userInput,
      researchStatus,
      currentCitations,
      streamingProgress,
      viewMode,
      activeProjectId,
      pendingMessage,

      // Multi-Agent Case Panel State
      agentSessions,
      activeAgentId,

      // Patient Clinical Data State
      patientData,
      isPatientDataLoading,
      isPatientHeaderExpanded,
      showPatientDataInput,
      isReconciling,
      patientPanelMode,

      // Actions
      addMessage,
      updateMessage,
      appendToLastMessage,
      addArtifact,
      updateArtifact,
      addFile,
      removeFile,
      clearFiles,
      transcribeAudio,
      setContext,
      setPatient,
      loadSession,
      loadSessionForWorkspace,
      removeWorkspaceConversation,
      createNewChat,
      clearSessionError,
      triggerSidebarRefresh,
      renameSession,
      togglePanel,
      setStreaming,
      setGenerating,
      expandArtifact,
      setCommandPickerOpen,
      setCommandPrefix: setCommandPrefixState,
      setUserInput,
      sendMessage,
      stopGeneration,
      clearChat,
      retryFromMessage,
      editMessage,
      editingMessageId,
      openProject,
      closeProject,
      startChatInProject,
      startChatInProjectWithMessage,
      loadSessionFromProjectView,
      setPendingMessage,

      // Patient Data Actions
      loadPatientData,
      setPatientDataDirect: setPatientData,
      parseAndSavePatientText,
      parseAndSavePatientTextForWorkspace,
      uploadAndSaveCCDA,
      clearPatientData,
      togglePatientHeader,
      setShowPatientDataInput,
      reconcilePatientData,
      togglePatientPanelMode,

      // Notification callback
      setNotificationCallback,
      setRefreshClinicalDataCallback,
      setRefreshArtifactCallback,
      setCanvasUpdateCallback,

      // Multi-Agent Case Panel Actions
      sendToAgent,
      switchAgent,
      getSessionMessages,
      isAgentResponding,
      initAgentSession,
      resetAgentSession,
      clearAgentSessions,
      loadAgentSessions,
      setActiveWorkspaceId: (id: string | null) => { activeWorkspaceIdRef.current = id }
    }}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within ChatProvider')
  }
  return context
}


